import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Public API instance — no auth token, no 401-redirect. Used by /aep public
// self-service page where 401 means "wrong NPN, let the user retry", not
// "kick to /login".
const publicApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password, device_token: localStorage.getItem('kr_device_token') || undefined }),
  twoFactorSend: (pending_token, channel) => api.post('/auth/2fa/send', { pending_token, channel }),
  twoFactorVerify: (pending_token, code, remember_device) => api.post('/auth/2fa/verify', { pending_token, code, remember_device }),
  activate: (token, email, password) => api.post('/auth/activate', { token, email, password }),
  me: () => api.get('/auth/me'),
  verifyInvitation: (token) => api.get(`/invitations/verify/${token}`),
  getContexts: () => api.get('/auth/contexts'),
  switchContext: (context_type, context_id) => api.post('/auth/switch-context', { context_type, context_id }),
  // Password reset (manual, admin-triggered or self-service)
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  checkResetToken: (token) => api.get(`/auth/reset-password/${token}/check`),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
  adminResetTemp: (user_id, notes) => api.post(`/admin/users/${user_id}/reset-password-temp`, { notes }),
  adminSendResetEmail: (user_id, notes) => api.post(`/admin/users/${user_id}/send-reset-email`, { notes }),
};

// JARVIS API admin (KR_SuperAdmin only — manage M2M API keys for the personal AI assistant)
export const jarvisAPI = {
  listKeys: () => api.get('/jarvis/admin/keys'),
  createKey: (name) => api.post('/jarvis/admin/keys', { name }),
  revokeKey: (key_id) => api.delete(`/jarvis/admin/keys/${key_id}`),
};

// AEP 2027 Certification Tracker — self-reported carrier certifications
export const aepAPI = {
  // Agent / self
  badgeCheck: () => api.get('/aep/badge/should-show'),
  myCertifications: () => api.get('/aep/my-certifications'),
  upsertMyCert: (carrier_id, status, notes) =>
    api.patch(`/aep/my-certifications/${carrier_id}`, { status, notes }),
  // GA/MGA/KR read view for a specific agent
  agentCertifications: (npn) => api.get(`/aep/agent/${npn}/certifications`),
  // Admin: carrier catalog
  adminListCarriers: (include_inactive = true) =>
    api.get('/aep/admin/carriers', { params: { include_inactive } }),
  adminCreateCarrier: (payload) => api.post('/aep/admin/carriers', payload),
  adminUpdateCarrier: (carrier_id, payload) =>
    api.patch(`/aep/admin/carriers/${carrier_id}`, payload),
  adminDeactivateCarrier: (carrier_id) =>
    api.delete(`/aep/admin/carriers/${carrier_id}`),
  // Phase 27.30.18 — Carrier logo upload/delete
  adminUploadCarrierLogo: (carrier_id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/aep/admin/carriers/${carrier_id}/logo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  adminDeleteCarrierLogo: (carrier_id) =>
    api.delete(`/aep/admin/carriers/${carrier_id}/logo`),
  // Admin: summary grid + CSV
  adminSummary: () => api.get('/aep/admin/summary'),
  adminSummaryCsvUrl: () => `${API_BASE}/aep/admin/summary.csv`,
  // Public /aep (no auth — NPN as PIN)
  publicSearch: (q) => publicApi.get('/aep/public/search', { params: { q } }),
  publicSession: (agent_ref, npn) => publicApi.post('/aep/public/session', { agent_ref, npn }),
  publicUpdateCert: (carrier_id, agent_ref, npn, status, notes) =>
    publicApi.patch(`/aep/public/certifications/${carrier_id}`, { agent_ref, npn, status, notes }),
  // Phase 27.38 — Public profile photo upload/delete gated by (agent_ref, npn)
  publicUploadPhoto: (agent_ref, npn, file) => {
    const fd = new FormData();
    fd.append('agent_ref', agent_ref);
    fd.append('npn', npn);
    fd.append('file', file);
    return publicApi.post('/aep/public/photo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  publicDeletePhoto: (agent_ref, npn) =>
    publicApi.delete('/aep/public/photo', { data: { agent_ref, npn } }),
};

// SuperAdmin Impersonation ("View As") API
export const impersonationAPI = {
  search: (q, kind = 'all', limit = 20) => api.get('/auth/impersonate/search', { params: { q, kind, limit } }),
  start: (target_type, target_id) => api.post('/auth/impersonate', { target_type, target_id }),
  exit: () => api.post('/auth/exit-impersonation'),
};

// Agent Portal API (self-service)
export const agentMeAPI = {
  getProfile: () => api.get('/agent/me'),
  updatePreferences: (payload) => api.patch('/agent/me/preferences', payload),
};

// Agency API
export const agencyAPI = {
  list: () => api.get('/agencies'),
  get: (id) => api.get(`/agencies/${id}`),
  create: (data) => api.post('/agencies', data),
  update: (id, data) => api.put(`/agencies/${id}`, data),
  delete: (id) => api.delete(`/agencies/${id}`),
  getTree: (id) => api.get(`/agencies/${id}/tree`),
  getSummary: (id) => api.get(`/agencies/${id}/summary`),
  getAgentCounts: () => api.get('/agencies/agent-counts'),
  getPrincipals: () => api.get('/agencies/principals'),
  // Communication Policy (Phase 20.8.2)
  getCommunicationPolicy: (id) =>
    api.get(`/agencies/${id}/communication-policy`),
  updateCommunicationPolicy: (id, payload) =>
    api.patch(`/agencies/${id}/communication-policy`, payload),
  requestCommunicationPolicyChange: (id, payload) =>
    api.post(`/agencies/${id}/communication-policy/request`, payload),
  // Communication Awareness Summary (Phase 20.8.5)
  getCommunicationSummary: (id) =>
    api.get(`/agencies/${id}/communication-summary`),
};

// User API
export const userAPI = {
  list: () => api.get('/users'),
  listPaginated: (params) => api.get('/users-paginated', { params }),
  get: (userId) => api.get(`/users/${userId}`),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  updateStatus: (userId, status) => api.put(`/users/${userId}/status`, { status }),
  assignRole: (data) => api.post('/users/roles', data),
  updateRoles: (userId, roles) => api.put(`/users/${userId}/roles`, { roles }),
  removeRole: (userId, roleId) => api.delete(`/users/${userId}/roles/${roleId}`),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Agent API
export const agentAPI = {
  list: (params) => api.get('/agents', { params }),
  listPaginated: (params) => api.get('/agents-paginated', { params }),
  getFilters: () => api.get('/agents-filters'),
  get: (npn) => api.get(`/agents/${npn}`),
  create: (data) => api.post('/agents', data),
  update: (npn, data) => api.put(`/agents/${npn}`, data),
  delete: (npn) => api.delete(`/agents/${npn}`),
  checkNpn: (npn) => api.get(`/agents/${npn}/check`),
  changeNPN: (old_npn, new_npn, reason) => api.post(`/agents/${old_npn}/change-npn`, { new_npn, reason }),
  revealSSN: (npn) => api.get(`/agents/${npn}/reveal-ssn`),
  getSSNAuditLog: (npn) => api.get(`/agents/${npn}/ssn-audit-log`),
  getExistingCarriers: (npn) => api.get(`/agents/${npn}/existing-carriers`),
  getHierarchy: (npn) => api.get(`/agents/${npn}/hierarchy`),
  getLinkedUser: (npn) => api.get(`/agents/${npn}/linked-user`),
  // Phase 27 — Direct portal-access generation (temp password = NPN).
  generateAccess: (npn) => api.post(`/agents/${npn}/generate-access`),
  // Phase 27.1 — Reset an activated agent's password back to their NPN.
  regenerateAccess: (npn) => api.post(`/agents/${npn}/regenerate-access`),
  bulkAction: (npns, action) => api.post('/agents/bulk-action', { npns, action }),
  transfer: (npn, data) => api.post(`/agents/${npn}/transfer`, data),
  transferHistory: (npn) => api.get(`/agents/${npn}/transfer-history`),
  timeline: (npn) => api.get(`/agents/${npn}/timeline`),
  simpleList: () => api.get('/agents-simple'),
};

// Agent self-service onboarding links (Add Agent → Send Link).
export const agentOnboardingAPI = {
  createLink: (data) => api.post('/agent-onboarding/links', data),
  listLinks: () => api.get('/agent-onboarding/links'),
  cancelLink: (linkId) => api.delete(`/agent-onboarding/links/${linkId}`),
  resendLink: (linkId) => api.post(`/agent-onboarding/links/${linkId}/resend`),
  getPublic: (token) => api.get(`/agent-onboarding/public/${token}`),
  submitPublic: (token, data) => api.post(`/agent-onboarding/public/${token}/submit`, data),
  reportProgress: (token, data) => api.post(`/agent-onboarding/public/${token}/progress`, data),
  // Agent-centric onboarding workspace (roster + checklist + graduate).
  roster: () => api.get('/agent-onboarding/roster'),
  agentDetail: (npn) => api.get(`/agent-onboarding/agents/${npn}`),
  inviteDetail: (linkId) => api.get(`/agent-onboarding/invites/${linkId}`),
  deleteInvite: (linkId) => api.delete(`/agent-onboarding/invites/${linkId}`),
  updateInviteEmail: (linkId, email) => api.patch(`/agent-onboarding/invites/${linkId}/email`, { email }),
  deleteAgent: (npn) => api.delete(`/agent-onboarding/agents/${npn}`),
  verifyNpn: (npn, verified) => api.post(`/agent-onboarding/agents/${npn}/verify-npn`, { verified }),
  setWelcomeEmail: (npn, sent) => api.post(`/agent-onboarding/agents/${npn}/welcome-email`, { sent }),
  graduate: (npn) => api.post(`/agent-onboarding/agents/${npn}/graduate`),
};

// Public agent onboarding applications (/apply → KR review queue).
export const agentApplicationAPI = {
  list: (params) => api.get('/agent-applications', { params }),
  get: (id) => api.get(`/agent-applications/${id}`),
  approve: (id, agencyId) => api.post(`/agent-applications/${id}/approve`, { agency_id: agencyId }),
  reject: (id, reason) => api.post(`/agent-applications/${id}/reject`, { reason }),
  pendingCount: () => api.get('/agent-applications/pending-count'),
};


// Agent self-service "Update your info" links (Agent profile → More Actions).
// Agency self-service "Update agency info" links (Agency profile → More Actions).
export const agencyUpdateAPI = {
  createLink: (agency_id) => api.post('/agency-updates/links', { agency_id }),
  getPublic: (token) => api.get(`/agency-updates/public/${token}`),
  submitPublic: (token, data) => api.post(`/agency-updates/public/${token}/submit`, data),
};

export const agentUpdateAPI = {
  createLink: (npn) => api.post('/agent-updates/links', { npn }),
  getPublic: (token) => api.get(`/agent-updates/public/${token}`),
  submitPublic: (token, data) => api.post(`/agent-updates/public/${token}/submit`, data),
};

// Compensation API (Phase 3 visibility)
export const myCompensationAPI = {
  get: (statement_month) => api.get('/compensation/my-compensation', {
    params: statement_month ? { statement_month } : {},
  }),
};

// Carrier API
export const carrierAPI = {
  list: () => api.get('/carriers'),
  create: (data) => api.post('/carriers', data),
  update: (id, data) => api.put(`/carriers/${id}`, data),
  delete: (id) => api.delete(`/carriers/${id}`),
};

// Contract Type API
export const contractTypeAPI = {
  list: (carrierId) => api.get('/contract-types', { params: { carrier_id: carrierId } }),
  create: (data) => api.post('/contract-types', data),
  update: (id, data) => api.put(`/contract-types/${id}`, data),
  delete: (id) => api.delete(`/contract-types/${id}`),
};

// Level API
export const levelAPI = {
  list: (carrierId) => api.get('/levels', { params: { carrier_id: carrierId } }),
  create: (data) => api.post('/levels', data),
  update: (id, data) => api.put(`/levels/${id}`, data),
  delete: (id) => api.delete(`/levels/${id}`),
  bulkSave: (levels, deleteIds = []) => api.post('/levels/bulk-save', { levels, delete_ids: deleteIds }),
  getAllowedLevels: (agencyId, carrierId) => api.get(`/allowed-levels/${agencyId}/${carrierId}`),
};

// Level Cap API
export const levelCapAPI = {
  list: (params) => api.get('/agency-level-caps', { params }),
  create: (data) => api.post('/agency-level-caps', data),
  update: (id, data) => api.put(`/agency-level-caps/${id}`, data),
  delete: (id) => api.delete(`/agency-level-caps/${id}`),
};

// Agency Contract Enablement API
export const agencyEnablementAPI = {
  list: (params) => api.get('/agency-contract-enablements', { params }),
  create: (data) => api.post('/agency-contract-enablements', data),
  update: (id, data) => api.put(`/agency-contract-enablements/${id}`, data),
  delete: (id) => api.delete(`/agency-contract-enablements/${id}`),
  getEnabledCarriers: (agencyId) => api.get(`/agencies/${agencyId}/enabled-carriers`),
};

// Agency Appointment API (agency-level contracts)
export const agencyAppointmentAPI = {
  list: (agencyId) => api.get(`/agencies/${agencyId}/appointments`),
  create: (agencyId, data) => api.post(`/agencies/${agencyId}/appointments`, data),
  update: (agencyId, appointmentId, data) => api.put(`/agencies/${agencyId}/appointments/${appointmentId}`, data),
  delete: (agencyId, appointmentId) => api.delete(`/agencies/${agencyId}/appointments/${appointmentId}`),
};

// Contract Request API
export const contractRequestAPI = {
  list: (params) => api.get('/contract-requests', { params }),
  listPaginated: (params) => api.get('/contracts-paginated', { params }),
  get: (id) => api.get(`/contract-requests/${id}`),
  create: (data) => api.post('/contract-requests', data),
  update: (id, data) => api.put(`/contract-requests/${id}`, data),
  updateStatus: (id, data) => api.post(`/contract-requests/${id}/workflow`, data),
  delete: (id, opts = {}) => api.delete(`/contract-requests/${id}`, {
    params: {
      // Explicit archive wins; force=true purges terminal requests
      // completely (bypassing the default soft-archive-on-terminal
      // safeguard on the backend).
      archive: !!opts.archive,
      force: !!opts.force,
    },
  }),
  hardDelete: (id) => api.delete(`/contract-requests/${id}`, { params: { force: true } }),
  archive: (id) => api.delete(`/contract-requests/${id}`, { params: { archive: true } }),
  // Delete many requests in one round-trip. `force` (default true)
  // purges terminal requests too — same semantics as `hardDelete`.
  bulkDelete: (ids, force = true) => api.post('/contract-requests/bulk-delete', {
    request_ids: ids,
    force,
  }),
  // Approve many requests in one round-trip. Skips incomplete (Missing Info)
  // and already-approved requests and reports them back.
  bulkApprove: (ids, comment) => api.post('/contract-requests/bulk-approve', {
    request_ids: ids,
    comment,
  }),
  // Release Follow-Up
  upsertReleaseFollowUp: (id, data) => api.post(`/contract-requests/${id}/release-followup`, data),
  completeReleaseFollowUp: (id, data) => api.post(`/contract-requests/${id}/release-followup/complete`, data),
  // Phase 27.53.F — Notes + Email-to-Note.
  listNotes: (id) => api.get(`/contract-requests/${id}/notes`),
  addNote: (id, content, visibility) => api.post(`/contract-requests/${id}/notes`, { content, visibility }),
  updateNoteVisibility: (id, noteId, visibility) =>
    api.patch(`/contract-requests/${id}/notes/${noteId}/visibility`, { visibility }),
  getInboundAddress: (id) => api.get(`/contract-requests/${id}/inbound-address`),
  reply: (id, payload) => api.post(`/contract-requests/${id}/reply`, payload),
};

// Release Follow-Up API (queue + summary) — legacy thin endpoints
export const releaseFollowUpAPI = {
  list: (params) => api.get('/release-followups', { params }),
  summary: () => api.get('/release-followups/summary'),
  runScheduler: () => api.post('/scheduler/run-release-followup-check'),
};

// Operations / Action Center
export const operationsAPI = {
  overview: () => api.get('/operations/overview'),
  actionCenter: () => api.get('/operations/action-center'),
};

// Per-request timeline + health score
export const contractTimelineAPI = {
  timeline: (requestId) => api.get(`/contract-requests/${requestId}/timeline`),
  healthScore: (requestId) => api.get(`/contract-requests/${requestId}/health-score`),
};

// Agent Getting Started checklist
export const agentChecklistAPI = {
  gettingStarted: (npn) => api.get(`/agents/${npn}/getting-started`),
};

// Unified Contract Follow-Up API — single source of truth for the activity layer
// (Agent → Request → [Contract] → Follow-Up).
export const contractFollowUpAPI = {
  list: (params) => api.get('/contract-followups', { params }),
  summary: () => api.get('/contract-followups/summary'),
  create: (data) => api.post('/contract-followups', data),
  update: (id, data) => api.put(`/contract-followups/${id}`, data),
  complete: (id, data) => api.post(`/contract-followups/${id}/complete`, data),
  remove: (id) => api.delete(`/contract-followups/${id}`),
};

// Appointment API
export const appointmentAPI = {
  list: (params) => api.get('/appointments', { params }),
  getAgentAppointments: (npn) => api.get(`/appointments/agent/${npn}`),
  create: (data) => api.post('/appointments', data),
  createDirect: (data) => api.post('/appointments/direct', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  delete: (id, archive = false) => api.delete(`/appointments/${id}`, { params: { archive } }),
  archive: (id) => api.delete(`/appointments/${id}`, { params: { archive: true } }),
};

// Ticket API
export const ticketAPI = {
  list: (params) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  delete: (id) => api.delete(`/tickets/${id}`),
  addNote: (id, content, visibility) => api.post(`/tickets/${id}/notes`, { content, visibility }),
  getNotes: (id) => api.get(`/tickets/${id}/notes`),
  updateNoteVisibility: (ticketId, noteId, visibility) =>
    api.patch(`/tickets/${ticketId}/notes/${noteId}/visibility`, { visibility }),
  snooze: (id, until) => api.post(`/tickets/${id}/snooze`, { until }),
  getInboundAddress: (id) => api.get(`/tickets/${id}/inbound-address`),
  reply: (id, payload) => api.post(`/tickets/${id}/reply`, payload),
  // Tasks
  listTasks: (id) => api.get(`/tickets/${id}/tasks`),
  createTask: (id, data) => api.post(`/tickets/${id}/tasks`, data),
  updateTask: (ticketId, taskId, data) => api.put(`/tickets/${ticketId}/tasks/${taskId}`, data),
  deleteTask: (ticketId, taskId) => api.delete(`/tickets/${ticketId}/tasks/${taskId}`),
};

// Invitation API
export const invitationAPI = {
  list: () => api.get('/invitations'),
  create: (data) => api.post('/invitations', data),
  resend: (id) => api.post(`/invitations/${id}/resend`),
  expire: (id) => api.delete(`/invitations/${id}`),
};

// Notification API
export const notificationAPI = {
  list: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Phase 27.29 — Google Calendar OAuth integration (one-way sync)
export const googleCalendarAPI = {
  status: () => api.get('/integrations/google/status'),
  getAuthUrl: (returnTo) => api.get('/integrations/google/auth-url', { params: returnTo ? { return_to: returnTo } : {} }),
  disconnect: () => api.post('/integrations/google/disconnect'),
  // Phase 27.30.14 — Diagnostics + manual resync
  diagnostics: () => api.get('/integrations/google/diagnostics'),
  resyncEvent: (eventId) => api.post(`/integrations/google/resync/event/${eventId}`),
  resyncFollowUp: (followUpId) => api.post(`/integrations/google/resync/follow-up/${followUpId}`),
  resyncPhoto: () => api.post('/integrations/google/resync-photo'),
};

// Phase 27.29.2 — Agent profile photo upload (custom picture)
export const agentPhotoAPI = {
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/agent/me/photo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  remove: () => api.delete('/agent/me/photo'),
  // Phase 27.30.22 — KR admin can upload/remove on behalf of any agent
  adminUpload: (npn, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/agents/${npn}/photo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  adminRemove: (npn) => api.delete(`/agents/${npn}/photo`),
};

// Compliance API
export const complianceAPI = {
  get: (npn) => api.get(`/agents/${npn}/compliance`),
  update: (npn, requirementKey, data) => api.put(`/agents/${npn}/compliance/${requirementKey}`, data),
  summary: () => api.get('/compliance/summary'),
};



// Internal Notes API
export const notesAPI = {
  list: (entityType, entityId) => api.get(`/notes/${entityType}/${entityId}`),
  create: (entityType, entityId, data) => api.post(`/notes/${entityType}/${entityId}`, data),
  delete: (noteId) => api.delete(`/notes/${noteId}`),
};

// Announcements API
export const announcementAPI = {
  list: () => api.get('/announcements'),
  create: (data) => api.post('/announcements', data),
  update: (id, data) => api.put(`/announcements/${id}`, data),
  delete: (id) => api.delete(`/announcements/${id}`),
  uploadCover: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/announcements/${id}/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteCover: (id) => api.delete(`/announcements/${id}/cover`),
};

export const dashboardBannerAPI = {
  active: () => api.get('/dashboard-banners/active'),
  list: () => api.get('/dashboard-banners'),
  create: (data) => api.post('/dashboard-banners', data),
  update: (id, data) => api.put(`/dashboard-banners/${id}`, data),
  delete: (id) => api.delete(`/dashboard-banners/${id}`),
  uploadImage: (id, slot, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/dashboard-banners/${id}/image?slot=${slot}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteImage: (id, slot) => api.delete(`/dashboard-banners/${id}/image?slot=${slot}`),
};

export const seasonalThemeAPI = {
  active: () => api.get('/seasonal-themes/active', { params: { _t: Date.now() } }),
  list: () => api.get('/seasonal-themes', { params: { _t: Date.now() } }),
  create: (data) => api.post('/seasonal-themes', data),
  update: (id, data) => api.put(`/seasonal-themes/${id}`, data),
  delete: (id) => api.delete(`/seasonal-themes/${id}`),
  activate: (id) => api.post(`/seasonal-themes/${id}/activate`),
  deactivate: (id) => api.post(`/seasonal-themes/${id}/deactivate`),
  schedule: (id, data) => api.post(`/seasonal-themes/${id}/schedule`, data),
  unschedule: (id) => api.post(`/seasonal-themes/${id}/unschedule`),
  uploadImage: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/seasonal-themes/${id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteImage: (id) => api.delete(`/seasonal-themes/${id}/image`),
  uploadSidebarImage: (id, file, original) => {
    const fd = new FormData();
    fd.append('file', file);
    if (original) fd.append('original', original);
    return api.post(`/seasonal-themes/${id}/sidebar-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteSidebarImage: (id) => api.delete(`/seasonal-themes/${id}/sidebar-image`),
};

export const recognitionAPI = {
  listPrograms: () => api.get('/recognition/programs'),
  getProgram: (id) => api.get(`/recognition/programs/${id}`),
  getPrivate: (id) => api.get(`/recognition/programs/${id}/private`),
  createProgram: (data) => api.post('/recognition/programs', data),
  updateProgram: (id, data) => api.put(`/recognition/programs/${id}`, data),
  duplicateProgram: (id) => api.post(`/recognition/programs/${id}/duplicate`),
  archiveProgram: (id, hard = false) => api.delete(`/recognition/programs/${id}?hard=${hard}`),
  uploadCover: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/recognition/programs/${id}/cover`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadMedia: (id, bucket, file, caption = '') => {
    const fd = new FormData(); fd.append('file', file);
    const qs = `bucket=${bucket}${caption ? `&caption=${encodeURIComponent(caption)}` : ''}`;
    return api.post(`/recognition/programs/${id}/media?${qs}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteMedia: (id, mediaId) => api.delete(`/recognition/programs/${id}/media/${mediaId}`),
  listParticipants: (id) => api.get(`/recognition/programs/${id}/participants`),
  addParticipant: (id, data) => api.post(`/recognition/programs/${id}/participants`, data),
  decideParticipant: (pid, data) => api.put(`/recognition/participants/${pid}`, data),
  removeParticipant: (pid) => api.delete(`/recognition/participants/${pid}`),
  respond: (id, data) => api.post(`/recognition/programs/${id}/respond`, data),
  agentSearch: (q) => api.get(`/recognition/agent-search?q=${encodeURIComponent(q)}`),
  getAudit: (id) => api.get(`/recognition/programs/${id}/audit`),
};

// Dashboard API
export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
  recentRequests: (limit = 10) => api.get('/dashboard/recent-requests', { params: { limit } }),
  activityFeed: (limit = 30) => api.get('/dashboard/activity-feed', { params: { limit } }),
  charts: () => api.get('/dashboard/charts'),
  upcomingBirthdays: (days = 30) => api.get('/dashboard/upcoming-birthdays', { params: { days } }),
};

// Phase 27.31 — Lead Landing Pages (admin CRUD + public form).
// Public endpoints are absolute-path so they bypass auth interceptors
// cleanly; admin ones use the authenticated `api` client.
export const leadLandingPagesAPI = {
  list: () => api.get('/lead-landing-pages'),
  get: (id) => api.get(`/lead-landing-pages/${id}`),
  create: (payload) => api.post('/lead-landing-pages', payload),
  update: (id, payload) => api.patch(`/lead-landing-pages/${id}`, payload),
  remove: (id) => api.delete(`/lead-landing-pages/${id}`),
  checkSlug: (slug) => api.get('/lead-landing-pages/slug-available', { params: { slug } }),
  options: () => api.get('/lead-landing-pages/options'),
  mine: () => api.get('/lead-landing-pages/mine'),
  mineList: () => api.get('/lead-landing-pages/mine-list'),
  analytics: (id, days = 30) => api.get(`/lead-landing-pages/${id}/analytics`, { params: { days } }),
  createMine: (payload) => api.post('/lead-landing-pages/mine', payload),
  updateMine: (payload) => api.patch('/lead-landing-pages/mine', payload),
  uploadPhoto: (id, formData) => api.post(
    `/lead-landing-pages/${id}/marketing-photo`, formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  uploadAgencyLogo: (id, formData) => api.post(
    `/lead-landing-pages/${id}/agency-logo`, formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  removeAgencyLogo: (id) => api.delete(`/lead-landing-pages/${id}/agency-logo`),
  uploadTeamMemberPhoto: (id, npn, formData) => api.post(
    `/lead-landing-pages/${id}/team/${npn}/photo`, formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  removeTeamMemberPhoto: (id, npn) => api.delete(`/lead-landing-pages/${id}/team/${npn}/photo`),
  leads: (id) => api.get(`/lead-landing-pages/${id}/leads`),
  activity: (id) => api.get(`/lead-landing-pages/${id}/activity`),
  reassignHistorical: (id, payload) => api.post(`/lead-landing-pages/${id}/reassign-historical`, payload),
  // Central consent management (versioned).
  consentCurrent: () => api.get('/lead-landing-pages/consent/current'),
  consentHistory: () => api.get('/lead-landing-pages/consent/history'),
  publishConsent: (payload) => api.post('/lead-landing-pages/consent', payload),
};

// Operations Pulse — lightweight contracting SLA counts for the Action Center.
export const operationsPulseAPI = {
  contractingActions: () => api.get('/operations/contracting-actions'),
};

// Public (no auth) — used by the /connect/:slug page.
export const publicLandingPagesAPI = {
  get: (slug) => publicApi.get(`/public/connect/${slug}`),
  submit: (slug, payload) => publicApi.post(`/public/connect/${slug}/submit`, payload),
  // Appointments — Phase 27.32.
  getAppointmentConfig: (slug) => publicApi.get(`/public/connect/${slug}/appointment-config`),
  getTeamRoster: (slug) => publicApi.get(`/public/connect/${slug}/team-roster`),
  getAppointmentSlots: (slug, date) => publicApi.get(`/public/connect/${slug}/appointment-slots?date=${date}`),
  bookAppointment: (slug, payload) => publicApi.post(`/public/connect/${slug}/appointment`, payload),
  // Team pages — per-agent availability configured on the team landing.
  getTeamMemberConfig: (slug, npn) => publicApi.get(`/public/connect/${slug}/team/${npn}/appointment-config`),
  getTeamMemberSlots: (slug, npn, date) => publicApi.get(`/public/connect/${slug}/team/${npn}/appointment-slots?date=${date}`),
  bookTeamMemberAppointment: (slug, npn, payload) => publicApi.post(`/public/connect/${slug}/team/${npn}/appointment`, payload),
};

// Agent/Admin-side Appointments (Phase 27.32).
export const appointmentsAPI = {
  list: (params) => api.get('/lead-appointments', { params }),
  confirm: (id) => api.patch(`/lead-appointments/${id}/confirm`),
  reschedule: (id, scheduled_at) => api.patch(`/lead-appointments/${id}/reschedule`, { scheduled_at }),
  cancel: (id) => api.patch(`/lead-appointments/${id}/cancel`),
  // Phase 27.34 — agent-initiated appointment for an existing lead
  // (works for leads that never came through a landing page).
  createForLead: (payload) => api.post('/lead-appointments/from-lead', payload),
  createForClient: (payload) => api.post('/lead-appointments/from-client', payload),
};

// Phase 27.33 — Admin-managed catalog of external enrollment
// platform links, surfaced by the global FAB launcher.
export const enrollmentPlatformsAPI = {
  list: () => api.get('/enrollment-platforms'),
  create: (payload) => api.post('/enrollment-platforms', payload),
  update: (id, payload) => api.patch(`/enrollment-platforms/${id}`, payload),
  remove: (id) => api.delete(`/enrollment-platforms/${id}`),
  uploadLogo: (id, formData) => api.post(
    `/enrollment-platforms/${id}/logo`, formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  removeLogo: (id) => api.delete(`/enrollment-platforms/${id}/logo`),
};

// Push Notification API
export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-public-key'),
  subscribe: (subscription) => api.post('/push/subscribe', { subscription }),
  unsubscribe: (subscription) => api.post('/push/unsubscribe', { subscription }),
  test: () => api.post('/push/test'),
};

// Weekly Digest API
export const digestAPI = {
  getConfig: () => api.get('/digest/config'),
  updateConfig: (data) => api.put('/digest/config', data),
  sendNow: () => api.post('/digest/send-now'),
};

// Global Search API
export const searchAPI = {
  search: (q) => api.get('/search', { params: { q } }),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Admin API
export const adminAPI = {
  cleanupDuplicates: () => api.post('/admin/cleanup-duplicates'),
  cleanupAgentDuplicates: (npn) => api.post(`/admin/cleanup-agent-duplicates/${npn}`),
  getNotifPrefs: () => api.get('/notification-preferences'),
  updateNotifPrefs: (data) => api.put('/notification-preferences', data),
};

// HubSpot API
export const hubspotAPI = {
  connectionStatus: () => api.get('/hubspot/status'),
  agentSyncStatus: (npn) => api.get(`/hubspot/agent-sync/${npn}`),
  triggerAgentSync: (npn) => api.post(`/hubspot/agent-sync/${npn}`),
  triggerBulkSync: () => api.post('/hubspot/bulk-sync'),
  getSyncLogs: (params) => api.get('/hubspot/sync-logs', { params }),
  getSyncSummary: () => api.get('/hubspot/sync-summary'),
};

// Audit Log API
export const auditAPI = {
  getLogs: (params) => api.get('/audit-logs', { params }),
  getFilters: () => api.get('/audit-logs/filters'),
};

// Calendar & Events (Phase 21.6A)
export const calendarAPI = {
  enums: () => api.get('/calendar/enums'),
  categories: () => api.get('/calendar/categories'),
  listEvents: (params) => api.get('/calendar/events', { params }),
  getEvent: (id, params) => api.get(`/calendar/events/${id}`, { params }),
  listParticipants: (id) => api.get(`/calendar/events/${id}/participants`),
  updateParticipantStatus: (id, userId, status) =>
    api.patch(`/calendar/events/${id}/participants/${userId}`, { status }),
  listAttachments: (id) => api.get(`/calendar/events/${id}/attachments`),
  downloadAttachment: (id, attachmentId) =>
    `${api.defaults.baseURL}/calendar/events/${id}/attachments/${attachmentId}/download`,
  flyerUrl: (id) => `${api.defaults.baseURL}/calendar/events/${id}/flyer`,
  previewVisibility: (id, proposed = {}) =>
    api.post(`/calendar/events/${id}/preview-visibility`, proposed || {}),
  previewAudience: (id) =>
    api.get(`/calendar/events/${id}/participants/audience-preview`),
  // Phase 21.6B — Administration + Lifecycle
  createEvent: (payload) => api.post('/calendar/events', payload),
  updateEvent: (id, patch) => api.patch(`/calendar/events/${id}`, patch),
  publish: (id) => api.post(`/calendar/events/${id}/publish`),
  cancel: (id, reason) => api.post(`/calendar/events/${id}/cancel`, { reason }),
  complete: (id) => api.post(`/calendar/events/${id}/complete`),
  archive: (id, reason) =>
    api.post(`/calendar/events/${id}/archive`, reason ? { reason } : {}),
  // Phase 21.6C.1 — Participant mutations
  inviteParticipants: (id, userIds, notes, opts = {}) =>
    api.post(`/calendar/events/${id}/participants`, {
      user_ids: userIds,
      notes,
      register_directly: !!opts.register_directly,
    }),
  inviteAllEligible: (id, notes) =>
    api.post(`/calendar/events/${id}/participants`, {
      invite_all_eligible_audience: true,
      notes,
    }),
  removeParticipant: (id, userId) =>
    api.delete(`/calendar/events/${id}/participants/${userId}`),
  // Phase 21.6C.2 — Attachment mutations
  uploadAttachment: (id, formData) =>
    api.post(`/calendar/events/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateAttachment: (id, attachmentId, patch) =>
    api.patch(`/calendar/events/${id}/attachments/${attachmentId}`, patch),
  deleteAttachment: (id, attachmentId) =>
    api.delete(`/calendar/events/${id}/attachments/${attachmentId}`),
  // Phase 26.2A — Registration & RSVP
  register: (id, body = {}) => api.post(`/calendar/events/${id}/register`, body),
  rsvp: (id, response) => api.post(`/calendar/events/${id}/rsvp`, { rsvp_response: response }),
  cancelRegistration: (id) => api.post(`/calendar/events/${id}/cancel-registration`),
  registrationSummary: (id) => api.get(`/calendar/events/${id}/registration-summary`),
  // Phase 2 — participation Slots
  listSlots: (id) => api.get(`/events/${id}/slots`),
  joinSlot: (id, slotId) => api.post(`/events/${id}/slots/${slotId}/join`),
  leaveSlot: (id, slotId) => api.delete(`/events/${id}/slots/${slotId}/leave`),
  listSlotSignups: (id) => api.get(`/events/${id}/slots/signups`),
  approveSlotSignup: (id, signupId) => api.post(`/events/${id}/slots/signups/${signupId}/approve`),
  rejectSlotSignup: (id, signupId) => api.post(`/events/${id}/slots/signups/${signupId}/reject`),
  removeSlotSignup: (id, signupId) => api.delete(`/events/${id}/slots/signups/${signupId}`),
  // Phase 26.2B — Participant management
  manageParticipant: (id, assignmentId, patch) =>
    api.patch(`/calendar/events/${id}/participants/${assignmentId}/manage`, patch),
  // Phase 26.3 — Attendance & Check-in
  checkIn: (id, assignmentId) =>
    api.post(`/calendar/events/${id}/participants/${assignmentId}/check-in`),
  undoCheckIn: (id, assignmentId) =>
    api.post(`/calendar/events/${id}/participants/${assignmentId}/undo-check-in`),
  markNoShow: (id, assignmentId) =>
    api.post(`/calendar/events/${id}/participants/${assignmentId}/mark-no-show`),
  bulkAttendance: (id, action, assignmentIds) =>
    api.post(`/calendar/events/${id}/participants/bulk-attendance`, {
      action, assignment_ids: assignmentIds,
    }),
  addWalkIn: (id, payload) =>
    api.post(`/calendar/events/${id}/walk-in`, payload),
  // Phase 26.4 — Waitlist
  waitlistPromote: (id, aid) =>
    api.post(`/calendar/events/${id}/waitlist/${aid}/promote`),
  waitlistRemove: (id, aid) =>
    api.post(`/calendar/events/${id}/waitlist/${aid}/remove`),
  waitlistMove: (id, aid, direction) =>
    api.post(`/calendar/events/${id}/waitlist/${aid}/move`, { direction }),
  // Phase 26.5 — Notifications
  notificationsHistory: (id, params = {}) =>
    api.get(`/calendar/events/${id}/notifications`, { params }),
  notificationResend: (id, notificationId) =>
    api.post(`/calendar/events/${id}/notifications/${notificationId}/resend`),
  publishBroadcastDiagnostic: (id, email) =>
    api.get(`/calendar/events/${id}/publish-broadcast-diagnostic`, {
      params: email ? { email } : {},
    }),
  // Phase 27.30.12 — Custom composer + Activity timeline
  notificationCompose: (id, payload) =>
    api.post(`/calendar/events/${id}/notifications/custom`, payload),
  notificationTemplates: (id) =>
    api.get(`/calendar/events/${id}/notification-templates`),
  activity: (id, params = {}) =>
    api.get(`/calendar/events/${id}/activity`, { params }),
  // Phase 27.30.16 — Share link
  shareLinkCreate: (id) => api.post(`/calendar/events/${id}/share-link`),
  shareLinkRevoke: (id) => api.delete(`/calendar/events/${id}/share-link`),
  participantsCsvUrl: (id, filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return `${api.defaults.baseURL}/calendar/events/${id}/participants/export.csv${qs ? `?${qs}` : ''}`;
  },
};

// Phase 26.2A — Public (anonymous) event registration
export const publicCalendarAPI = {
  register: (eventId, payload) =>
    api.post(`/public/calendar/events/${eventId}/register`, payload),
  getEvent: (eventId) => api.get(`/public/calendar/events/${eventId}`),
};

// Phase 27.37.A — Twilio SMS integration (KR admin only).
export const smsIntegrationAPI = {
  status: () => api.get('/integrations/twilio/status'),
  webhooks: () => api.get('/integrations/twilio/webhooks'),
  trusthubRefresh: () => api.post('/integrations/twilio/trusthub/refresh'),
  testSms: (payload) => api.post('/integrations/twilio/test-sms', payload),
  // Phase 27.37.O — pull the live delivery status of a specific Twilio message.
  messageStatus: (sid) => api.get(`/integrations/twilio/message-status/${encodeURIComponent(sid)}`),
  // Phase 27.37.I — inbound webhook signature diagnostics.
  inboundDiagnostics: (params = {}) => api.get('/integrations/twilio/diagnostics/inbound', { params }),
  // Phase 27.37.M — per-phone trace: where does this number live in the SMS pipeline?
  phoneDiagnostics: (phone) => api.get(`/integrations/twilio/diagnostics/phone/${encodeURIComponent(phone)}`),
};

// Phase 27.37.B — SMS messaging (Lead/Prospect/Agent drawer + consent + templates).
export const smsMessagingAPI = {
  conversation: (contactType, contactId) => api.get('/messaging/sms/conversation', { params: { contact_type: contactType, contact_id: contactId } }),
  preview: (body) => api.post('/messaging/sms/preview', { body }),
  segments: (body) => api.post('/messaging/sms/preview', { body }),
  send: (payload) => api.post('/messaging/sms/send', payload),
  senders: () => api.get('/messaging/sms/senders'),
  // Phase 27.37.N — Per-agent Lead SMS permission read.
  permissions: () => api.get('/messaging/sms/permissions'),
  consentLanguage: (language = 'en') => api.get('/messaging/sms/consent-language', { params: { language } }),
  optIn: (payload) => api.post('/messaging/sms/consent/opt-in', payload),
  optOut: (payload) => api.post('/messaging/sms/consent/opt-out', payload),
  templates: (params) => api.get('/messaging/sms/templates', { params }),
  createTemplate: (payload) => api.post('/messaging/sms/templates', payload),
  updateTemplate: (id, payload) => api.patch(`/messaging/sms/templates/${id}`, payload),
  deleteTemplate: (id) => api.delete(`/messaging/sms/templates/${id}`),
  // Phase 27.57 — presence heartbeat so inbound alerts are suppressed
  // while the operator is actively viewing this conversation.
  presence: (contactType, contactId) => api.post('/messaging/sms/presence', { contact_type: contactType, contact_id: contactId }),
};

// Phase 27.58 — Twilio Programmable Voice (browser softphone, KR admins).
export const voiceAPI = {
  settings: () => api.get('/voice/settings'),
  updateSettings: (payload) => api.patch('/voice/settings', payload),
  token: () => api.post('/voice/token'),
  presence: (available = true) => api.post('/voice/presence', null, { params: { available } }),
  online: () => api.get('/voice/online'),
  supervision: () => api.get('/voice/supervision'),
  usage: (days = 30) => api.get('/voice/usage', { params: { days } }),
  assignableAgents: () => api.get('/voice/assignable-agents'),
  softphoneAgents: () => api.get('/voice/softphone-agents'),
  transferStart: (data) => api.post('/voice/transfer/start', data),
  transferComplete: (data) => api.post('/voice/transfer/complete', data),
  pauseRecording: (data) => api.post('/voice/recording/pause', data),
  provision: () => api.post('/voice/provision'),
  syncNumbers: () => api.post('/voice/sync-numbers'),
  numberHealth: () => api.get('/voice/number-health'),
  fixNumberHealth: () => api.post('/voice/number-health/fix'),
  // Teams / ring groups
  listTeams: () => api.get('/voice/teams'),
  createTeam: (payload) => api.post('/voice/teams', payload),
  updateTeam: (id, payload) => api.put(`/voice/teams/${id}`, payload),
  deleteTeam: (id) => api.delete(`/voice/teams/${id}`),
  calls: (params) => api.get('/voice/calls', { params }),
  addCallNote: (payload) => api.post('/voice/call-note', payload),
  voicemails: () => api.get('/voice/voicemails'),
  markVoicemailRead: (callSid) => api.post(`/voice/voicemails/${callSid}/read`),
  lookup: (number) => api.get('/voice/lookup', { params: { number } }),
  recordingStreamUrl: (recordingSid) => `${API_BASE}/voice/recordings/${recordingSid}/stream`,
  // Call Flows (Aircall-style visual call routing)
  listFlows: () => api.get('/voice/flows'),
  getFlow: (id) => api.get(`/voice/flows/${id}`),
  createFlow: (payload) => api.post('/voice/flows', payload),
  updateFlow: (id, payload) => api.put(`/voice/flows/${id}`, payload),
  deleteFlow: (id) => api.delete(`/voice/flows/${id}`),
  flowNumbers: () => api.get('/voice/flows/available-numbers'),
};

// Phase 27.59 — Unified Communication Log (emails + SMS).
export const commLogAPI = {
  list: (params) => api.get('/comm-log', { params }),
};

// Phase 27.61 — Academy (training courses).
export const academyAPI = {
  listCourses: () => api.get('/academy/courses'),
  getCourse: (id) => api.get(`/academy/courses/${id}`),
  createCourse: (payload) => api.post('/academy/courses', payload),
  updateCourse: (id, payload) => api.patch(`/academy/courses/${id}`, payload),
  deleteCourse: (id) => api.delete(`/academy/courses/${id}`),
  completeLesson: (courseId, lessonId) => api.post(`/academy/courses/${courseId}/lessons/${lessonId}/complete`),
  submitExam: (courseId, payload) => api.post(`/academy/courses/${courseId}/submit-exam`, payload),
  myBadges: () => api.get('/academy/my-badges'),
  agentBadges: (npn) => api.get(`/academy/agents/${npn}/badges`),
  getCertificate: (id) => api.get(`/academy/certificates/${id}`),
  courseProgress: (id) => api.get(`/academy/courses/${id}/progress`),
  ai: (id, payload) => api.post(`/academy/courses/${id}/ai`, payload),
};

export const raffleAPI = {
  list: () => api.get('/raffles'),
  get: (id) => api.get(`/raffles/${id}`),
  create: (data) => api.post('/raffles', data),
  update: (id, data) => api.patch(`/raffles/${id}`, data),
  remove: (id) => api.delete(`/raffles/${id}`),
  assignableAgents: () => api.get('/raffles/assignable-agents'),
  forLead: (leadId) => api.get(`/raffles/for-lead/${leadId}`),
  enroll: (id, lead_id, ticket_number) => api.post(`/raffles/${id}/enroll`, { lead_id, ticket_number }),
  removeEntry: (id, entryId) => api.delete(`/raffles/${id}/entries/${entryId}`),
  draw: (id, mode, lead_id, opts = {}) => api.post(`/raffles/${id}/draw`, { mode, lead_id, ...opts }),
  removeWinner: (id, leadId) => api.delete(`/raffles/${id}/winners/${leadId}`),
  setWinnerPrize: (id, leadId, prize_label) => api.patch(`/raffles/${id}/winners/${leadId}`, { prize_label }),
};

export const reimbursementAPI = {
  uploadReceipt: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/reimbursements/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  create: (data) => api.post('/reimbursements', data),
  mine: () => api.get('/reimbursements/mine'),
  list: (status) => api.get('/reimbursements', { params: { status: status || undefined } }),
  get: (id) => api.get(`/reimbursements/${id}`),
  receiptUrl: (id, storageKey) => `/api/reimbursements/${id}/receipts/${storageKey}`,
  cancel: (id) => api.post(`/reimbursements/${id}/cancel`),
  review: (id) => api.post(`/reimbursements/${id}/review`),
  approve: (id, data = {}) => api.post(`/reimbursements/${id}/approve`, data),
  reject: (id, reason) => api.post(`/reimbursements/${id}/reject`, { reason }),
  markPaid: (id, data = {}) => api.post(`/reimbursements/${id}/mark-paid`, data),
};

export const retentionAPI = {
  opportunities: (category, q) => api.get('/retention/opportunities', { params: { category, q: q || undefined } }),
  kpis: () => api.get('/retention/kpis'),
  seasons: () => api.get('/retention/seasons'),
  createSeason: (data) => api.post('/retention/seasons', data),
  updateSeason: (id, data) => api.patch(`/retention/seasons/${id}`, data),
  deleteSeason: (id) => api.delete(`/retention/seasons/${id}`),
  settings: () => api.get('/retention/settings'),
  updateSettings: (data) => api.patch('/retention/settings', data),
  aiBrief: (client_id, opportunity) => api.post('/retention/ai-brief', { client_id, opportunity }),
  markWorked: (data) => api.post('/retention/worked', data),
  listWorked: (category) => api.get('/retention/worked', { params: { category: category || undefined } }),
  undoWorked: (workedId) => api.delete(`/retention/worked/${encodeURIComponent(workedId)}`),
};

export const needsAnalysisAPI = {
  getQuestionsConfig: () => api.get('/needs-analysis/questions'),
  updateQuestionsConfig: (sets) => api.put('/needs-analysis/questions', { sets }),
  getForLead: (leadId) => api.get(`/leads/${leadId}/needs-analysis`),
  saveForLead: (leadId, data) => api.put(`/leads/${leadId}/needs-analysis`, data),
  aiSummary: (leadId) => api.post(`/leads/${leadId}/needs-analysis/ai-summary`),
  downloadPdf: (leadId) => api.get(`/leads/${leadId}/needs-analysis/pdf`, { responseType: 'blob' }),
  saveClientDoctorsMeds: (clientId, data) => api.put(`/clients/${clientId}/needs-analysis/doctors-meds`, data),
};


export const clientsAPI = {
  list: (params = {}) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (payload) => api.post('/clients', payload),
  update: (id, payload) => api.patch(`/clients/${id}`, payload),
  archive: (id) => api.delete(`/clients/${id}`),
  restore: (id) => api.post(`/clients/${id}/restore`),
  revealSsn: (id) => api.get(`/clients/${id}/ssn`),
  activity: (id) => api.get(`/clients/${id}/activity`),
  addNote: (id, note) => api.post(`/clients/${id}/activity`, { note }),
  convertFromLead: (leadId) => api.post(`/clients/from-lead/${leadId}`),
  importFields: () => api.get('/clients/import/fields'),
  importPreview: (formData) => api.post('/clients/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importCommit: (payload) => api.post('/clients/import/commit', payload),
  listFollowUps: (id) => api.get(`/clients/${id}/follow-ups`),
  createFollowUp: (id, scheduled_at, note) => api.post(`/clients/${id}/follow-ups`, { scheduled_at, note }),
  completeFollowUp: (fid) => api.post(`/clients/follow-ups/${fid}/complete`),
  deleteFollowUp: (fid) => api.delete(`/clients/follow-ups/${fid}`),
  uploadPhoto: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/clients/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deletePhoto: (id) => api.delete(`/clients/${id}/photo`),
  photoBlob: (id) => api.get(`/clients/${id}/photo`, { responseType: 'blob' }),
  listDocuments: (id) => api.get(`/clients/${id}/documents`),
  uploadDocument: (id, file, category = 'other') => {
    const fd = new FormData(); fd.append('file', file); fd.append('category', category);
    return api.post(`/clients/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadDocument: (id, docId) => api.get(`/clients/${id}/documents/${docId}/download`, { responseType: 'blob' }),
  updateDocument: (id, docId, category) => api.patch(`/clients/${id}/documents/${docId}`, { category }),
  deleteDocument: (id, docId) => api.delete(`/clients/${id}/documents/${docId}`),
  exportCsv: (params = {}) => api.get('/clients/export', { params, responseType: 'blob' }),
  pdf: (id) => api.get(`/clients/${id}/pdf`, { responseType: 'blob' }),
};

export const smsInboxAPI = {
  list: (params = {}) => api.get('/messaging/sms/inbox', { params }),
  counts: () => api.get('/messaging/sms/inbox/counts'),
  markRead: (cid) => api.post(`/messaging/sms/inbox/${cid}/mark-read`),
  link: (cid, payload) => api.post(`/messaging/sms/inbox/${cid}/link`, payload),
  dismiss: (cid) => api.post(`/messaging/sms/inbox/${cid}/dismiss`),
  messages: (cid) => api.get(`/messaging/sms/inbox/${cid}/messages`),
  reply: (cid, payload) => api.post(`/messaging/sms/inbox/${cid}/reply`, payload),
};

// Phase 27.37.G — Agent SMS Broadcasts (KR admin only).
export const smsBroadcastAPI = {
  preview: (audience) => api.post('/messaging/sms/broadcasts/preview', { audience }),
  create: (payload) => api.post('/messaging/sms/broadcasts', payload),
  list: (params = {}) => api.get('/messaging/sms/broadcasts', { params }),
  get: (id) => api.get(`/messaging/sms/broadcasts/${id}`),
  cancel: (id) => api.post(`/messaging/sms/broadcasts/${id}/cancel`),
  dispatchNow: (id) => api.post(`/messaging/sms/broadcasts/${id}/dispatch-now`),
};

// Phase 27.37.J/K — SMS AI Copilot + CRM Note Polish.
export const smsCopilotAPI = {
  suggest: (payload) => api.post('/messaging/sms/copilot/suggest', payload),
  // Phase 27.37.O — First-touch welcome opener for new leads.
  welcome: (payload) => api.post('/messaging/sms/copilot/welcome', payload),
  markUsed: (payload) => api.post('/messaging/sms/copilot/used', payload),
  audit: (params = {}) => api.get('/messaging/sms/copilot/audit', { params }),
  crmNotePolish: (payload) => api.post('/messaging/sms/copilot/crm-note', payload),
  crmNotePolishUsed: (payload) => api.post('/messaging/sms/copilot/crm-note/used', payload),
};

// Phase 27.38 — Knowledge base (KR-curated YouTube / Google Drive resources).
export const knowledgeAPI = {
  list: (params = {}) => api.get('/knowledge', { params }),
  get: (id) => api.get(`/knowledge/${id}`),
  create: (payload) => api.post('/knowledge', payload),
  update: (id, payload) => api.patch(`/knowledge/${id}`, payload),
  remove: (id) => api.delete(`/knowledge/${id}`),
  uploadCover: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/knowledge/${id}/cover`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteCover: (id) => api.delete(`/knowledge/${id}/cover`),
};

// Phase 27.38.2 — SMS Usage weekly report.
export const smsUsageAPI = {
  report: (windowDays = 7) => api.get('/messaging/sms/usage', { params: { window_days: windowDays } }),
  agent: (npn, windowDays = 7) => api.get(`/messaging/sms/usage/agent/${npn}`, { params: { window_days: windowDays } }),
};

// Phase 27.39 — Team directory (non-agent staff).
export const teamAPI = {
  list: (params = {}) => api.get('/team/members', { params }),
  get: (userId) => api.get(`/team/members/${userId}`),
  createManual: (payload) => api.post('/team/members', payload),
  sendInvite: (payload) => api.post('/team/members/invite', payload),
  update: (userId, payload) => api.patch(`/team/members/${userId}`, payload),
  uploadPhoto: (userId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/team/members/${userId}/photo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (userId) => api.delete(`/team/members/${userId}/photo`),
  // Phase 27.39.2 — Unified Google Calendar aggregation across team members.
  calendarEvents: (params = {}) => api.get('/team/calendar/events', { params }),
};

// Prospects API (Phase 22 — public "Become an Agent" backing)
export const prospectAPI = {
  list: (params) => api.get('/prospects', { params }),
  get: (id) => api.get(`/prospects/${id}`),
  update: (id, patch) => api.patch(`/prospects/${id}`, patch),
  // Phase 27.37.P — KR admins can remove a prospect from the pipeline.
  remove: (id) => api.delete(`/prospects/${id}`),
  // Phase 27.30.31 — Manual create for KR staff to seed the pipeline.
  create: (payload) => api.post('/prospects', payload),
  // Phase 27.36 — Look up the prospect linked to an event registration
  // row (participant_assignments). Used by the Event Workspace to open
  // the Prospect Drawer directly from a prospect badge.
  byAssignment: (assignmentId) => api.get(`/prospects/by-assignment/${assignmentId}`),
  // Phase 2 — manual follow-up reminders with a due date.
  addReminder: (id, data) => api.post(`/prospects/${id}/reminders`, data),
  completeReminder: (id, reminderId) => api.post(`/prospects/${id}/reminders/${reminderId}/complete`),
  deleteReminder: (id, reminderId) => api.delete(`/prospects/${id}/reminders/${reminderId}`),
  // Phase 3 — recruiting emails from the single KR recruiting mailbox.
  emailTemplates: () => api.get('/prospects/email/templates'),
  sendEmail: (id, payload) => api.post(`/prospects/${id}/send-email`, payload),
  // Phase 4 — convert a recruited prospect into a pending agent onboarding.
  convert: (id, payload) => api.post(`/prospects/${id}/convert`, payload),
  // Advanced recruiting call wrap-up (outcome + stage + call-back) from softphone.
  logCall: (id, payload) => api.post(`/prospects/${id}/call-log`, payload),
  // Onboarding invite status issued at conversion (pending/completed/expired).
  onboardingInvite: (id) => api.get(`/prospects/${id}/onboarding-invite`),
};

// Phase 27.37.Q — KR Referral Partner Program.
export const referralAPI = {
  rules: () => api.get('/referrals/rules'),
  agreement: () => api.get('/referrals/agreement'),
  stages: () => api.get('/referrals/stages'),
  eligibility: () => api.get('/referrals/eligibility'),
  eligibleReferrers: () => api.get('/referrals/eligible-referrers'),
  leaderboard: (limit) => api.get('/referrals/leaderboard', { params: { limit } }),
  list: (params) => api.get('/referrals', { params }),
  get: (id) => api.get(`/referrals/${id}`),
  create: (payload) => api.post('/referrals', payload),
  approve: (id, payload) => api.patch(`/referrals/${id}/approve`, payload || {}),
  reject: (id, payload) => api.patch(`/referrals/${id}/reject`, payload),
  updateStage: (id, payload) => api.patch(`/referrals/${id}/stage`, payload),
  addPolicy: (id, payload) => api.post(`/referrals/${id}/policies`, payload),
  updatePolicy: (id, pid, payload) => api.patch(`/referrals/${id}/policies/${pid}`, payload),
  deletePolicy: (id, pid) => api.delete(`/referrals/${id}/policies/${pid}`),
  finalize: (id) => api.post(`/referrals/${id}/finalize`),
  // Phase 27.37.T — CSV export with live points, level, and bonus per referral.
  exportCsv: (params) => api.get('/referrals/export/csv', { params, responseType: 'blob' }),
};

// Phase 27.37.V — KR-tunable auto-reminder cadence.
export const cadenceAPI = {
  get: () => api.get('/settings/auto-reminders'),
  update: (payload) => api.patch('/settings/auto-reminders', payload),
  preview: () => api.get('/settings/auto-reminders/nurture-preview'),
};

// Phase 27.37.V — Kora chatbot analytics (KR-only dashboard).
export const koraAPI = {
  analytics: () => api.get('/kora/analytics'),
  getTrainingConfig: () => api.get('/kora/training-config'),
  updateTrainingConfig: (payload) => api.put('/kora/training-config', payload),
  previewTraining: (payload) => api.post('/kora/training-config/preview', payload),
};

// Communication Policy Governance (Phase 20.8.3, extended in 20.8.4/20.8.6)
export const communicationGovernanceAPI = {
  listRequests: () => api.get('/governance/communication-policy/requests'),
  getSummary: () => api.get('/governance/communication-policy/summary'),
  // Agency-level policy requests
  approve: (agencyId) =>
    api.post(`/governance/communication-policy/request/${agencyId}/approve`),
  reject: (agencyId, reason) =>
    api.post(`/governance/communication-policy/request/${agencyId}/reject`, { reason }),
  // Agent-level exception requests (Phase 20.8.4)
  approveAgentException: (npn) =>
    api.post(`/governance/communication-policy/request/agent/${npn}/approve`),
  rejectAgentException: (npn, reason) =>
    api.post(`/governance/communication-policy/request/agent/${npn}/reject`, { reason }),
};

// Agent Communication Exception (Phase 20.8.4)
export const agentCommunicationExceptionAPI = {
  get: (npn) => api.get(`/agents/${npn}/communication-exception`),
  set: (npn, payload) => api.patch(`/agents/${npn}/communication-exception`, payload),
  remove: (npn) => api.delete(`/agents/${npn}/communication-exception`),
  request: (npn, payload) => api.post(`/agents/${npn}/communication-exception/request`, payload),
};

// Scheduler API
export const schedulerAPI = {
  runInvoiceCheck: () => api.post('/scheduler/run-invoice-check'),
};

// Email Templates API
export const emailTemplateAPI = {
  list: () => api.get('/email-templates'),
  get: (id) => api.get(`/email-templates/${id}`),
  create: (data) => api.post('/email-templates', data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
  seedDefaults: () => api.post('/email-templates/seed-defaults'),
};

// Reports API
export const reportsAPI = {
  summary: (params) => api.get('/reports/summary', { params }),
  exportAgentsCSV: (params) => api.get('/reports/agents-csv', { responseType: 'blob', params }),
  exportContractsCSV: (params) => api.get('/reports/contracts-csv', { responseType: 'blob', params }),
  exportAgenciesCSV: (params) => api.get('/reports/agencies-csv', { responseType: 'blob', params }),
  exportBillingCSV: () => api.get('/reports/billing-csv', { responseType: 'blob' }),
  exportAgentsPDF: (params) => api.get('/reports/agents-pdf', { params, responseType: 'blob' }),
  exportContractsPDF: (params) => api.get('/reports/contracts-pdf', { params, responseType: 'blob' }),
};

// Unified Reports Hub (catalog + preview + export CSV/Excel/PDF + AI)
export const reportsHubAPI = {
  catalog: () => api.get('/reports-hub/catalog'),
  preview: (key, params) => api.get(`/reports-hub/${key}/preview`, { params }),
  exportReport: (key, format, params) =>
    api.get(`/reports-hub/${key}/export`, { params: { ...(params || {}), format }, responseType: 'blob' }),
  ai: (prompt) => api.post('/reports-hub/ai', { prompt }),
};

// Onboarding API
export const onboardingAPI = {
  checklist: () => api.get('/agent/onboarding-checklist'),
  dismiss: () => api.post('/agent/onboarding-checklist/dismiss'),
};

// Contract Catalog API
export const catalogAPI = {
  flatCatalog: () => api.get('/contracts/catalog'),
};

// Smart Compliance API
export const smartComplianceAPI = {
  get: (npn) => api.get(`/agents/${npn}/smart-compliance`),
  update: (npn, code, data) => api.put(`/agents/${npn}/smart-compliance/${code}`, data),
  // Phase 27.12 — one-click friendly-reminder completion. Body: { expiration_date }
  markComplete: (npn, code, data) => api.post(`/agents/${npn}/smart-compliance/${code}/mark-complete`, data),
};

// Agency Performance API
export const agencyPerformanceAPI = {
  scoreboard: () => api.get('/performance/scoreboard'),
  auditLogs: (params) => api.get('/performance/audit-logs', { params }),
};

export default api;

// ── Payout Accounting API ──
export const payoutAPI = {
  // Ledger
  ledger: {
    list: (params) => api.get('/payout/ledger', { params }),
    get: (id) => api.get(`/payout/ledger/${id}`),
    create: (data) => api.post('/payout/ledger', data),
    update: (id, data) => api.put(`/payout/ledger/${id}`, data),
    delete: (id) => api.delete(`/payout/ledger/${id}`),
    bulkCreate: (entries) => api.post('/payout/ledger/bulk', entries),
    stats: () => api.get('/payout/ledger-stats'),
    markPaid: (data) => api.post('/payout/ledger/mark-paid', data),
    assign: (ledgerId, statementId) => api.post(`/payout/ledger/${ledgerId}/assign`, { statement_id: statementId }),
  },
  // Payee Lookup
  payees: (params) => api.get('/payout/payees', { params }),
  // Import
  import: {
    upload: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post('/payout/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    confirm: (batchId, columnMapping) => api.post('/payout/import/confirm', { batch_id: batchId, column_mapping: columnMapping }),
    batches: () => api.get('/payout/import-batches'),
  },
  // Statements
  statements: {
    list: (params) => api.get('/payout/statements', { params }),
    get: (id) => api.get(`/payout/statements/${id}`),
    generate: (data) => api.post('/payout/statements/generate', data),
    approve: (id, action) => api.put(`/payout/statements/${id}/approve`, { action }),
    downloadPdf: (id) => api.get(`/payout/statements/${id}/pdf`, { responseType: 'blob' }),
  },
  // Payments
  payments: {
    list: (params) => api.get('/payout/payments', { params }),
    create: (data) => api.post('/payout/payments', data),
    updateStatus: (id, status, notes) => api.put(`/payout/payments/${id}/status`, { status, notes }),
  },
  // Reconciliation
  reconciliation: {
    overview: (params) => api.get('/payout/reconciliation', { params }),
    exportCsv: (month) => api.get('/payout/reconciliation/export', { params: { statement_month: month }, responseType: 'blob' }),
  },
  // EFT / Payment Info
  eft: {
    list: (params) => api.get('/payout/eft', { params }),
    get: (id) => api.get(`/payout/eft/${id}`),
    submit: (data) => api.post('/payout/eft', data),
    review: (id, action, notes) => api.put(`/payout/eft/${id}/review`, { action, notes }),
    history: (payeeType, payeeId) => api.get(`/payout/eft/history/${payeeType}/${payeeId}`),
  },
};

// Override Reconciliation API (NEW SYSTEM)
export const overrideAPI = {
  // Batches
  batches: {
    list: (params) => api.get('/override/batches', { params }),
    get: (id) => api.get(`/override/batches/${id}`),
    create: (formData) => api.post('/override/batches', formData, { 
      headers: { 'Content-Type': 'multipart/form-data' } 
    }),
    import: (batchId) => api.post(`/override/batches/${batchId}/import`),
    updateStatus: (id, status) => api.put(`/override/batches/${id}/status`, { status }),
    archive: (id, reason) => api.post(`/override/batches/${id}/archive`, { reason }),
    download: (id) => api.get(`/override/batches/${id}/download`, { responseType: 'blob' }),
    delete: (id) => api.delete(`/override/batches/${id}`),
  },
  // Details
  details: {
    list: (params) => api.get('/override/details', { params }),
    get: (id) => api.get(`/override/details/${id}`),
    update: (id, data) => api.put(`/override/details/${id}`, data),
    exclude: (detailIds) => api.post('/override/details/exclude', { detail_ids: detailIds }),
    createManual: (data) => api.post('/override/details/manual', data),
    delete: (id) => api.delete(`/override/details/${id}`),
  },
  // Allocations
  allocations: {
    list: (params) => api.get('/override/allocations', { params }),
    get: (id) => api.get(`/override/allocations/${id}`),
    create: (data) => api.post('/override/allocations', data),
    createBulk: (allocations) => api.post('/override/allocations/bulk', { allocations }),
    update: (id, data) => api.put(`/override/allocations/${id}`, data),
    approve: (allocationIds, action, notes) => api.post('/override/allocations/approve', { 
      allocation_ids: allocationIds, 
      action, 
      notes 
    }),
    delete: (id) => api.delete(`/override/allocations/${id}`),
  },
  // Statements
  statements: {
    list: (params) => api.get('/override/statements', { params }),
    get: (id) => api.get(`/override/statements/${id}`),
    generate: (statementMonth, payeeIds) => api.post('/override/statements/generate', { 
      statement_month: statementMonth, 
      payee_ids: payeeIds 
    }),
    void: (id, reason) => api.post(`/override/statements/${id}/void`, { reason }),
    downloadPdf: (id) => api.get(`/override/statements/${id}/pdf`, { responseType: 'blob' }),
    delete: (id) => api.delete(`/override/statements/${id}`),
  },
  // Payments
  payments: {
    list: (params) => api.get('/override/payments', { params }),
    get: (id) => api.get(`/override/payments/${id}`),
    create: (data) => api.post('/override/payments', data),
    updateStatus: (id, status, notes) => api.put(`/override/payments/${id}/status`, { status, notes }),
    delete: (id) => api.delete(`/override/payments/${id}`),
  },
  // Reconciliation / Dashboard
  reconciliation: (params) => api.get('/override/reconciliation', { params }),
  months: () => api.get('/override/months'),
};


// Kanban Board API
export const kanbanAPI = {
  getBoard: () => api.get('/kanban/board'),
};

// Platform Settings API
export const settingsAPI = {
  getCategories: () => api.get('/platform-settings/categories'),
  updateCategories: (data) => api.put('/platform-settings/categories', data),
};

// Follow-ups API
export const followUpAPI = {
  list: (params) => api.get('/follow-ups', { params }),
  summary: () => api.get('/follow-ups/summary'),
  create: (data) => api.post('/follow-ups', data),
  complete: (id) => api.put(`/follow-ups/${id}/complete`),
  reopen: (id) => api.put(`/follow-ups/${id}/reopen`),
  delete: (id) => api.delete(`/follow-ups/${id}`),
};

// ─── Phase 23.1 — CRM Notes & Unified Follow-ups ─────────────────
export const crmNotesAPI = {
  list: ({ subject_type, subject_id, include_deleted = false, page = 1, page_size = 50 }) =>
    api.get('/crm/notes', { params: { subject_type, subject_id, include_deleted, page, page_size } }),
  get: (noteId) => api.get(`/crm/notes/${noteId}`),
  create: (data) => api.post('/crm/notes', data),
  remove: (noteId, deletion_reason) => api.delete(`/crm/notes/${noteId}`, { data: { deletion_reason } }),
  uploadAttachment: (noteId, formData) =>
    api.post(`/crm/notes/${noteId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  attachmentUrl: (noteId, attachmentId) => `/crm/notes/${noteId}/attachments/${attachmentId}`,
  activity: ({ subject_type, subject_id, page = 1, page_size = 50 }) =>
    api.get('/crm/activity', { params: { subject_type, subject_id, page, page_size } }),
};

export const crmFollowupsAPI = {
  list: (params) => api.get('/crm/followups', { params }),
  summary: () => api.get('/crm/followups/summary'),
  get: (id) => api.get(`/crm/followups/${id}`),
  create: (data) => api.post('/crm/followups', data),
  patch: (id, data) => api.patch(`/crm/followups/${id}`, data),
  addProgress: (id, note) => api.post(`/crm/followups/${id}/progress`, { note }),
  deleteProgress: (id, updateId, deletion_reason) =>
    api.delete(`/crm/followups/${id}/progress/${updateId}`, { data: { deletion_reason } }),
  complete: (id, data) => api.post(`/crm/followups/${id}/complete`, data),
};

// ─── Phase 24 — Contract Unification (KR admin) ─────────────────
export const contractsAdminAPI = {
  duplicateGroups: () => api.get('/admin/contracts/duplicate-groups'),
  resolveGroup: (payload) => api.post('/admin/contracts/duplicate-groups/resolve', payload),
  needsReviewSummary: () => api.get('/admin/contracts/needs-review/summary'),
  dismissNeedsReview: (requestId, review_notes) =>
    api.post(`/admin/contracts/${requestId}/needs-review/dismiss`, { review_notes }),
};

// ─── Phase 25.1 + 25.1.1 — Accounting: Carrier Imports + Mapping ─────────
export const accountingAPI = {
  listImports: (params) => api.get('/accounting/carrier-imports', { params }),
  getImport: (id) => api.get(`/accounting/carrier-imports/${id}`),
  createImport: (formData) =>
    api.post('/accounting/carrier-imports', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // 27.10 Fase B — Manual Payment (JSON body, no file upload)
  createManualPayment: (payload) => api.post('/accounting/carrier-imports/manual', payload),
  fileUrl: (id) => `/accounting/carrier-imports/${id}/file`,
  listLines: (id, params) => api.get(`/accounting/carrier-imports/${id}/lines`, { params }),
  updateLine: (id, lineId, data) =>
    api.patch(`/accounting/carrier-imports/${id}/lines/${lineId}`, data),
  setStatus: (id, status) => api.post(`/accounting/carrier-imports/${id}/status`, { status }),
  archiveImport: (id) => api.delete(`/accounting/carrier-imports/${id}`),
  // 25.1.1 — manual column mapping + templates
  preview: (id, params) => api.get(`/accounting/carrier-imports/${id}/preview`, { params }),
  applyMapping: (id, payload) => api.post(`/accounting/carrier-imports/${id}/apply-mapping`, payload),
  // Agent name/alias resolution (statements without NPN, e.g. Humana)
  unmatchedAgents: (id) => api.get(`/accounting/carrier-imports/${id}/unmatched-agents`),
  assignAgent: (id, payload) => api.post(`/accounting/carrier-imports/${id}/assign-agent`, payload),
  // Agency Medicare commission schedules (override grid)
  getAgencySchedule: (agencyId) => api.get('/accounting/agency-commission-schedules', { params: { agency_id: agencyId } }),
  saveAgencySchedule: (agencyId, payload) => api.put(`/accounting/agency-commission-schedules/${agencyId}`, payload),
  // Bank deposit confirmation (gate before paying agencies)
  getDeposit: (id) => api.get(`/accounting/carrier-imports/${id}/deposit`),
  setDeposit: (id, payload) => api.post(`/accounting/carrier-imports/${id}/deposit`, payload),
  // Unreceived commission report (deposit not confirmed)
  unreceivedReport: (params) => api.get('/accounting/reports/unreceived-commission', { params }),
  unreceivedReportFile: (params) => api.get('/accounting/reports/unreceived-commission', { params, responseType: 'blob' }),
  // Self-service statements list for a recipient (owner or KR/Accounting)
  recipientStatements: (recipientType, recipientId, params) =>
    api.get(`/accounting/recipients/${recipientType}/${recipientId}/statements`, { params }),
  // Grouped bank deposits (one bank payment ↔ many imports)
  listBankDeposits: (params) => api.get('/accounting/bank-deposits', { params }),
  suggestBankDepositImports: (params) => api.get('/accounting/bank-deposits/suggest', { params }),
  createBankDeposit: (payload) => api.post('/accounting/bank-deposits', payload),
  getBankDeposit: (id) => api.get(`/accounting/bank-deposits/${id}`),
  updateBankDeposit: (id, payload) => api.put(`/accounting/bank-deposits/${id}`, payload),
  deleteBankDeposit: (id) => api.delete(`/accounting/bank-deposits/${id}`),
  listFields: () => api.get('/accounting/accounting-fields'),
  listTemplates: (params) => api.get('/accounting/mapping-templates', { params }),
  createTemplate: (data) => api.post('/accounting/mapping-templates', data),
  updateTemplate: (tid, data) => api.put(`/accounting/mapping-templates/${tid}`, data),
  deleteTemplate: (tid) => api.delete(`/accounting/mapping-templates/${tid}`),
  // 25.2 — reconciliation engine
  reconcile: (id) => api.post(`/accounting/carrier-imports/${id}/reconcile`),
  reconciliationSummary: (id) => api.get(`/accounting/carrier-imports/${id}/reconciliation-summary`),
  setLineRecipient: (id, lineId, payload) =>
    api.patch(`/accounting/carrier-imports/${id}/lines/${lineId}/recipient`, payload),
  setLineAmountToPay: (id, lineId, amount) =>
    api.patch(`/accounting/carrier-imports/${id}/lines/${lineId}/amount-to-pay`, { amount_to_pay: amount }),
  setLineCommissionPct: (id, lineId, pct) =>
    api.patch(`/accounting/carrier-imports/${id}/lines/${lineId}/commission-pct`, { commission_pct: pct }),
  bulkSetRecipient: (id, payload) =>
    api.post(`/accounting/carrier-imports/${id}/bulk-recipient`, payload),
  bulkSetCommissionPct: (id, payload) =>
    api.post(`/accounting/carrier-imports/${id}/bulk-commission-pct`, payload),
  listPlaceholders: (params) => api.get('/accounting/placeholders', { params }),
  // 25.3 — statement generation + recipient balance
  statementPreview: (id) => api.get(`/accounting/carrier-imports/${id}/statement-preview`),
  generateStatements: (id) => api.post(`/accounting/carrier-imports/${id}/generate-statements`),
  statementsForImport: (id) => api.get(`/accounting/carrier-imports/${id}/statements`),
  listStatements: (params) => api.get('/accounting/statements', { params }),
  getStatement: (sid) => api.get(`/accounting/statements/${sid}`),
  getStatementLines: (sid) => api.get(`/accounting/statements/${sid}/lines`),
  statementPdfUrl: (sid) => `/accounting/statements/${sid}/pdf`,
  recipientBalance: (recipientType, recipientId) =>
    api.get(`/accounting/recipients/${recipientType}/${recipientId}/balance`),
  recipientLedger: (recipientType, recipientId, params) =>
    api.get(`/accounting/recipients/${recipientType}/${recipientId}/ledger`, { params }),
  // 25.4A — payment info + eligibility + payment center
  getPaymentInfo: (recipientType, recipientId) =>
    api.get(`/accounting/payment-info/${recipientType}/${recipientId}`),
  updateW9: (recipientType, recipientId, data) =>
    api.patch(`/accounting/payment-info/${recipientType}/${recipientId}/w9`, data),
  updateDirectDeposit: (recipientType, recipientId, data) =>
    api.patch(`/accounting/payment-info/${recipientType}/${recipientId}/direct-deposit`, data),
  updateCheck: (recipientType, recipientId, data) =>
    api.patch(`/accounting/payment-info/${recipientType}/${recipientId}/check`, data),
  updatePreferredMethod: (recipientType, recipientId, method) =>
    api.patch(`/accounting/payment-info/${recipientType}/${recipientId}/preferred-method`, { preferred_method: method }),
  paymentCenterSummary: () => api.get('/accounting/payment-center/summary'),
  paymentCenterRecipients: (params) => api.get('/accounting/payment-center/recipients', { params }),
  paymentNotifications: (params) => api.get('/accounting/payment-notifications', { params }),
  // 25.4B — payment batches
  previewBatch: (payload) => api.post('/accounting/payment-batches/preview', payload),
  createBatch: (payload) => api.post('/accounting/payment-batches', payload),
  listBatches: (params) => api.get('/accounting/payment-batches', { params }),
  getBatch: (batchId) => api.get(`/accounting/payment-batches/${batchId}`),
  getBatchLines: (batchId) => api.get(`/accounting/payment-batches/${batchId}/lines`),
  submitBatch: (batchId, payload = {}) => api.post(`/accounting/payment-batches/${batchId}/submit`, payload),
  approveBatch: (batchId, payload = {}) => api.post(`/accounting/payment-batches/${batchId}/approve`, payload),
  markBatchReady: (batchId, payload = {}) => api.post(`/accounting/payment-batches/${batchId}/mark-ready`, payload),
  rejectBatch: (batchId, payload) => api.post(`/accounting/payment-batches/${batchId}/reject`, payload),
  cancelBatch: (batchId, payload = {}) => api.post(`/accounting/payment-batches/${batchId}/cancel`, payload),
  // 25.4C — payment execution
  setBatchLineMethod: (batchId, batchLineId, payload) =>
    api.patch(`/accounting/payment-batches/${batchId}/lines/${batchLineId}/method`, payload),
  markBatchPaid: (batchId) => api.post(`/accounting/payment-batches/${batchId}/mark-paid`),
  markBatchLineFailed: (batchId, batchLineId, payload) =>
    api.post(`/accounting/payment-batches/${batchId}/lines/${batchLineId}/mark-failed`, payload),
  recipientPaymentHistory: (recipientType, recipientId, params) =>
    api.get(`/accounting/recipients/${recipientType}/${recipientId}/payment-history`, { params }),
  // Phase 27.21 — Download a paid statement's PDF (agent-self, agency-owner,
  // KR, and Accounting authorized via `_owns_recipient`).
  downloadStatementPdf: (statementId) =>
    api.get(`/accounting/statements/${statementId}/pdf`, { responseType: 'blob' }),
  batchExportUrl: (batchId, variant = 'full') =>
    `/accounting/payment-batches/${batchId}/export?variant=${variant}`,
  // Phase 27.30.5 — Source imports for a payment batch (reverse nav).
  getBatchSources: (batchId) => api.get(`/accounting/payment-batches/${batchId}/sources`),

  // 25.4D — manual adjustments + ledger + history
  listAdjustments: (params) => api.get('/accounting/manual-adjustments', { params }),
  createAdjustment: (payload) => api.post('/accounting/manual-adjustments', payload),
  getAdjustment: (id) => api.get(`/accounting/manual-adjustments/${id}`),
  updateAdjustment: (id, payload) => api.patch(`/accounting/manual-adjustments/${id}`, payload),
  deleteAdjustment: (id) => api.delete(`/accounting/manual-adjustments/${id}`),
  submitAdjustment: (id) => api.post(`/accounting/manual-adjustments/${id}/submit`),
  approveAdjustment: (id) => api.post(`/accounting/manual-adjustments/${id}/approve`),
  cancelAdjustment: (id, payload = {}) => api.post(`/accounting/manual-adjustments/${id}/cancel`, payload),

  ledgerQuery: (params) => api.get('/accounting/ledger', { params }),
  ledgerExportUrl: (params) => {
    const q = new URLSearchParams({ format: 'csv', ...params }).toString();
    return `/accounting/ledger/export?${q}`;
  },
  recipientAccountingSummary: (recipientType, recipientId) =>
    api.get(`/accounting/recipients/${recipientType}/${recipientId}/accounting-summary`),
  accountingHistory: (params) => api.get('/accounting/history', { params }),
};



// Trainees API
export const traineeAPI = {
  list: (params) => api.get('/trainees', { params }),
  create: (data) => api.post('/trainees', data),
  update: (id, data) => api.put(`/trainees/${id}`, data),
  delete: (id) => api.delete(`/trainees/${id}`),
  convertToAgent: (id, npn) => api.post(`/trainees/${id}/convert-to-agent?npn=${npn}`),
  getNotes: (id) => api.get(`/trainees/${id}/notes`),
  addNote: (id, content) => api.post(`/trainees/${id}/notes`, { content }),
  sendNudge: (id) => api.post(`/trainees/${id}/send-nudge`),
  dashboard: () => api.get('/trainees/dashboard'),
};


// Managers / Development Leaders API
export const managersAPI = {
  list: (params) => api.get('/managers', { params }),
  get: (id) => api.get(`/managers/${id}`),
  create: (data) => api.post('/managers', data),
  update: (id, data) => api.put(`/managers/${id}`, data),
  remove: (id) => api.delete(`/managers/${id}`),
};


// Compensation Proration Engine API
export const compensationAPI = {
  summary: (params) => api.get('/compensation/summary', { params }),
  // Rules
  listRules: (params) => api.get('/compensation/rules', { params }),
  createRule: (data) => api.post('/compensation/rules', data),
  updateRule: (id, data) => api.patch(`/compensation/rules/${id}`, data),
  // Records
  listRecords: (params) => api.get('/compensation/records', { params }),
  getRecord: (id) => api.get(`/compensation/records/${id}`),
  createRecord: (data) => api.post('/compensation/records', data),
  updateRecord: (id, data) => api.patch(`/compensation/records/${id}`, data),
  // Statements — two-step preview/commit
  previewStatement: (formData) => api.post('/compensation/statements/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  commitStatement: (data) => api.post('/compensation/statements/commit', data),
  importStatement: (formData) => api.post('/compensation/statements/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteRecord: (record_id) => api.delete(`/compensation/records/${record_id}`),
  deleteStatement: (statement_id) => api.delete(`/compensation/statements/${statement_id}`),
  listStatements: (params) => api.get('/compensation/statements', { params }),
  // Templates
  listImportTemplates: (params) => api.get('/compensation/import-templates', { params }),
  createImportTemplate: (data) => api.post('/compensation/import-templates', data),
  deleteImportTemplate: (id) => api.delete(`/compensation/import-templates/${id}`),
  cloneImportTemplate: (id) => api.post(`/compensation/import-templates/${id}/clone`),
  activateImportTemplate: (id) => api.post(`/compensation/import-templates/${id}/activate`),
  // Mapping health
  mappingHealth: (params) => api.get('/compensation/mapping-health', { params }),
  // Reviews
  listReviews: (params) => api.get('/compensation/reviews', { params }),
  resolveReview: (id, payload) => api.post(`/compensation/reviews/${id}/resolve`, payload),
  // Adjustments
  listAdjustments: (params) => api.get('/compensation/adjustments', { params }),
  applyAdjustment: (id) => api.post(`/compensation/adjustments/${id}/apply`),
  // ─── Cycle / Statements / Payments (Phase B–C) ───
  getCycle: (offset = 0) => api.get('/compensation/cycle', { params: { offset } }),
  cycleDashboard: (params) => api.get('/compensation/cycle-dashboard', { params }),
  generatePayoutStatements: (data) => api.post('/compensation/payout-statements/generate', data),
  listPayoutStatements: (params) => api.get('/compensation/payout-statements', { params }),
  getPayoutStatement: (id) => api.get(`/compensation/payout-statements/${id}`),
  approvePayoutStatement: (id) => api.post(`/compensation/payout-statements/${id}/approve`),
  exportPayoutStatement: (id, format) => api.get(`/compensation/payout-statements/${id}/export`, {
    params: { format }, responseType: 'blob',
  }),
  schedulePayment: (data) => api.post('/compensation/payments/schedule', data),
  listPayments: (params) => api.get('/compensation/payments', { params }),
  sendPayment: (id) => api.post(`/compensation/payments/${id}/send`),
  completePayment: (id, data) => api.post(`/compensation/payments/${id}/complete`, data),
  failPayment: (id, data) => api.post(`/compensation/payments/${id}/fail`, data),
  emailPayoutStatement: (id, data) => api.post(`/compensation/payout-statements/${id}/email`, data),
};


export const marketingAPI = {
  listTemplates: (params) => api.get('/marketing/templates', { params }),
  getTemplate: (id) => api.get(`/marketing/templates/${id}`),
  uploadTemplate: (formData) => api.post('/marketing/templates', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateTemplate: (id, data) => api.put(`/marketing/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/marketing/templates/${id}`),
  placeOrder: (id, payload) => api.post(`/marketing/templates/${id}/order`, payload),
  listRequests: (params) => api.get('/marketing/requests', { params }),
  uploadFinal: (id, formData) => api.post(`/marketing/requests/${id}/final`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  declineRequest: (id, reason) => api.put(`/marketing/requests/${id}/decline`, { reason }),
  deleteRequest: (id, payload) => api.delete(`/marketing/requests/${id}`, { data: payload || {} }),
  downloadFile: (id) => api.get(`/marketing/requests/${id}/download`, { responseType: 'blob' }),
  previewBlob: (id, page = 1) => api.get(`/marketing/templates/${id}/preview`, { params: { page }, responseType: 'blob' }),
  coverImageBlob: (id) => api.get(`/marketing/templates/${id}/cover-image`, { responseType: 'blob' }),
  uploadCoverImage: (id, formData) => api.post(`/marketing/templates/${id}/cover-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteCoverImage: (id) => api.delete(`/marketing/templates/${id}/cover-image`),
  // Gallery
  uploadGalleryImage: (id, formData) => api.post(`/marketing/templates/${id}/gallery`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteGalleryImage: (id, imageId) => api.delete(`/marketing/templates/${id}/gallery/${imageId}`),
  reorderGallery: (id, imageIds) => api.put(`/marketing/templates/${id}/gallery/reorder`, { image_ids: imageIds }),
  galleryImageBlob: (id, imageId) => api.get(`/marketing/templates/${id}/gallery/${imageId}`, { responseType: 'blob' }),
  notifyTemplate: (id) => api.post(`/marketing/templates/${id}/notify`),
  checkNotifyRecipient: (id, email) => api.get(`/marketing/templates/${id}/notify/check`, { params: { email } }),
  suggestPrice: (id, cost) => api.post(`/marketing/templates/${id}/suggest-price`, { cost }),
  sendPromo: (payload) => api.post('/marketing/promo/send', payload),
  promoHistory: () => api.get('/marketing/promo/history'),
  cancelScheduledPromo: (id) => api.delete(`/marketing/promo/scheduled/${id}`),
  uploadPromoHero: (formData) => api.post('/marketing/promo/hero-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Attachments + agency brand
  uploadAttachment: (formData) => api.post('/marketing/uploads', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  attachmentBlob: (path) => api.get('/marketing/uploads/file', { params: { path }, responseType: 'blob' }),
  brandInfo: () => api.get('/marketing/brand-info'),
  uploadAgencyLogo: (agencyId, formData) => api.post(`/marketing/agencies/${agencyId}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAgencyLogo: (agencyId) => api.delete(`/marketing/agencies/${agencyId}/logo`),
};

export const disclaimerAPI = {
  status: () => api.get('/disclaimer/status'),
  accept: (version, signed_name) => api.post('/disclaimer/accept', { version, signed_name }),
  getConfig: () => api.get('/disclaimer/admin/config'),
  bumpVersion: (version, reason, content) => api.put('/disclaimer/admin/config', { version, reason, ...(content || {}) }),
  saveContent: (content) => api.put('/disclaimer/admin/content', content || {}),
  listAcceptances: (params) => api.get('/disclaimer/admin/acceptances', { params }),
};

// Agent Onboarding Workflow (Phase 7-10)
export const onboardingWorkflowAPI = {
  listRuns: (params) => api.get('/workflows/agent-onboarding/runs', { params }),
  getRun: (runId) => api.get(`/workflows/agent-onboarding/${runId}`),
  start: (payload) => api.post('/workflows/agent-onboarding/start', payload),
};

// Workflow Operational Settings (Phase 12/13) — KR admins only.
// Backend responds with rows enriched with `updated_by_name` so the UI
// never has to render raw user IDs.
export const workflowSettingsAPI = {
  list: (workflow_name) =>
    api.get('/admin/workflows/settings', {
      params: workflow_name ? { workflow_name } : {},
    }),
  update: (workflow_name, setting, value) =>
    api.put('/admin/workflows/settings', { workflow_name, setting, value }),
};

// Phase 27.26 — Leads Management
export const leadsAPI = {
  list: (params = {}) => api.get('/leads', { params }),
  get: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  revealSsn: (id) => api.get(`/leads/${id}/ssn`),
  uploadPhoto: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/leads/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deletePhoto: (id) => api.delete(`/leads/${id}/photo`),
  photoBlob: (id) => api.get(`/leads/${id}/photo`, { responseType: 'blob' }),
  listDocuments: (id) => api.get(`/leads/${id}/documents`),
  uploadDocument: (id, file, category = 'other') => {
    const fd = new FormData(); fd.append('file', file); fd.append('category', category);
    return api.post(`/leads/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadDocument: (id, docId) => api.get(`/leads/${id}/documents/${docId}/download`, { responseType: 'blob' }),
  updateDocument: (id, docId, category) => api.patch(`/leads/${id}/documents/${docId}`, { category }),
  deleteDocument: (id, docId) => api.delete(`/leads/${id}/documents/${docId}`),
  delete: (id) => api.delete(`/leads/${id}`),
  assign: (lead_ids, agent_npn) => api.post('/leads/assign', { lead_ids, agent_npn }),
  changeStatus: (id, status, notes) => api.post(`/leads/${id}/status`, { status, notes }),
  callDisposition: (id, disposition, note) => api.post(`/leads/${id}/call-disposition`, { disposition, note }),
  closeWon: (id, sales, notes) => api.post(`/leads/${id}/close-won`, { sales, notes }),
  notInterested: (id, reason, reason_details) =>
    api.post(`/leads/${id}/not-interested`, { reason, reason_details }),
  importFile: (formData) =>
    api.post('/leads/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importPreview: (formData) =>
    api.post('/leads/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  bulkDelete: (lead_ids) => api.post('/leads/bulk-delete', { lead_ids }),
  reports: () => api.get('/leads/reports/summary'),
  // Phase 27.27 — Activity Timeline + quick-note templates
  events: (id) => api.get(`/leads/${id}/events`),
  activity: (id) => api.get(`/leads/${id}/activity`),
  markSmsRead: (id) => api.post(`/leads/${id}/sms/mark-read`),
  addNote: (id, text, template_id) => api.post(`/leads/${id}/note`, { text, template_id }),
  noteTemplates: () => api.get('/leads/note-templates'),
  // Phase 27.28 Fase A — Follow-ups
  listFollowUps: (leadId) => api.get(`/leads/${leadId}/follow-ups`),
  createFollowUp: (leadId, scheduled_at, note) =>
    api.post(`/leads/${leadId}/follow-ups`, { scheduled_at, note }),
  updateFollowUp: (fid, patch) => api.put(`/leads/follow-ups/${fid}`, patch),
  completeFollowUp: (fid) => api.post(`/leads/follow-ups/${fid}/complete`),
  cancelFollowUp: (fid) => api.delete(`/leads/follow-ups/${fid}`),
  upcomingFollowUps: (limit = 20) => api.get('/leads/follow-ups/upcoming', { params: { limit } }),
  // Phase 27.43 — Duplicate detection.
  duplicates: (params) => api.get('/leads/duplicates', { params }),
  dismissDuplicateAlert: (id) => api.post(`/leads/${id}/dismiss-duplicate-alert`),
  merge: (sourceId, keeperId) =>
    api.post(`/leads/${sourceId}/merge`, { keeper_id: keeperId }),
  // Phase 27.37.R — AI Lead Summary (Claude Sonnet 5 via Emergent LLM).
  aiSummary: (id) => api.get(`/leads/${id}/ai-summary`),
  regenerateAiSummary: (id) => api.post(`/leads/${id}/ai-summary`),
};

// Phase 27.45 — Event Requests inside Marketing Center.
export const eventRequestsAPI = {
  enums: () => api.get('/event-requests/enums'),
  list: (params) => api.get('/event-requests', { params }),
  create: (payload) => api.post('/event-requests', payload),
  get: (id) => api.get(`/event-requests/${id}`),
  update: (id, payload) => api.patch(`/event-requests/${id}`, payload),
  submitAgencyReview: (id, note) => api.post(`/event-requests/${id}/submit-agency-review`, { note }),
  submitKrReview: (id, note) => api.post(`/event-requests/${id}/submit-kr-review`, { note }),
  krApprove: (id, payload) => api.post(`/event-requests/${id}/kr-approve`, payload),
  decline: (id, reason) => api.post(`/event-requests/${id}/decline`, { reason }),
  needsInfo: (id, note) => api.post(`/event-requests/${id}/needs-info`, { note }),
  complete: (id) => api.post(`/event-requests/${id}/complete`),
  // Phase 27.48 — flexibility (cancel/reopen/adjust/delete)
  cancel: (id, reason) => api.post(`/event-requests/${id}/cancel`, { reason: reason || null }),
  reopen: (id, note) => api.post(`/event-requests/${id}/reopen`, { note: note || null }),
  adjustApproval: (id, payload) => api.post(`/event-requests/${id}/adjust-approval`, payload),
  delete: (id, payload) => api.delete(`/event-requests/${id}`, { data: payload || {} }),
  reportOverview: (params) => api.get('/event-requests/reports/overview', { params }),
  reportSingle: (id) => api.get(`/event-requests/${id}/report`),
  leadMetrics: (id) => api.get(`/event-requests/${id}/lead-metrics`),
  leadOptions: () => api.get('/event-requests/lead-options'),
  updateParticipants: (id, agents) => api.post(`/event-requests/${id}/participants`, { agents }),
  uploadAttachment: (id, file, title, kind) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    fd.append('kind', kind || 'other');
    return api.post(`/event-requests/${id}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadAttachment: (id, idx) => api.get(
    `/event-requests/${id}/attachments/${idx}/download`,
    { responseType: 'blob' },
  ),
};

// Phase 27.30.6 — Broadcast email preferences (in-app toggle for /profile).
export const broadcastPreferencesAPI = {
  getMe: () => api.get('/me/broadcast-preferences'),
  setMe: (paused) => api.post('/me/broadcast-preferences', { paused: !!paused }),
};

// Phase 27.46 — Retail Program Requests (CVS × carriers).
export const retailProgramAPI = {
  enums: () => api.get('/retail-program/enums'),
  list: (params) => api.get('/retail-program', { params }),
  create: (payload) => api.post('/retail-program', payload),
  get: (id) => api.get(`/retail-program/${id}`),
  update: (id, payload) => api.patch(`/retail-program/${id}`, payload),
  submitKrReview: (id, note) => api.post(`/retail-program/${id}/submit-kr-review`, { note }),
  markSentToCarrier: (id, note) => api.post(`/retail-program/${id}/mark-sent-to-carrier`, { note }),
  recordSlotResponse: (id, slotId, payload) =>
    api.post(`/retail-program/${id}/slots/${slotId}/response`, payload),
  complete: (id) => api.post(`/retail-program/${id}/complete`),
  delete: (id, payload) => api.delete(`/retail-program/${id}`, { data: payload || {} }),
  agentOptions: () => api.get('/retail-program/agent-options'),
  downloadCarrierExport: (carrier) => api.get(
    `/retail-program/carriers/${carrier}/pending.xlsx`,
    { responseType: 'blob' },
  ),
  leadOptions: () => api.get('/retail-program/lead-options'),
  leadMetrics: (id) => api.get(`/retail-program/${id}/lead-metrics`),
  reportsOverview: (params) => api.get('/retail-program/reports/overview', { params }),
  // Managed Table Top / Marketing Site locations.
  listLocations: (params) => api.get('/retail-program/locations', { params }),
  getLocation: (id) => api.get(`/retail-program/locations/${id}`),
  createLocation: (payload) => api.post('/retail-program/locations', payload),
  updateLocation: (id, payload) => api.patch(`/retail-program/locations/${id}`, payload),
  deleteLocation: (id) => api.delete(`/retail-program/locations/${id}`),
  uploadLocationImage: (id, kind, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/retail-program/locations/${id}/image`, fd, {
      params: { kind },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLocationImage: (id, kind) => api.delete(`/retail-program/locations/${id}/image`, { params: { kind } }),
};

export const broadcastAPI = {
  audienceCounts: () => api.get('/broadcast/audience-counts'),
  previewRecipients: (payload) => api.post('/broadcast/preview-recipients', payload),
  ttsPreview: (payload) => api.post('/broadcast/tts-preview', payload),
  test: (payload) => api.post('/broadcast/test', payload),
  create: (payload) => api.post('/broadcast', payload),
  list: () => api.get('/broadcast'),
  get: (id) => api.get(`/broadcast/${id}`),
  sendNow: (id) => api.post(`/broadcast/${id}/send-now`),
  remove: (id) => api.delete(`/broadcast/${id}`),
  listTemplates: () => api.get('/broadcast/templates'),
  createTemplate: (payload) => api.post('/broadcast/templates', payload),
  deleteTemplate: (id) => api.delete(`/broadcast/templates/${id}`),
  callerNumbers: () => api.get('/broadcast/caller-numbers'),
  searchAgents: (q) => api.get('/broadcast/agents', { params: { q } }),
  agenciesTree: () => api.get('/broadcast/agencies-tree'),
  retryFailed: (id) => api.post(`/broadcast/${id}/retry-failed`),
  uploadAttachment: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/broadcast/upload-attachment', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadAudio: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/broadcast/upload-audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Phase 27.49 — QuickBooks OAuth (Sandbox / Production).
// KR SuperAdmin only. Handles the OAuth 2.0 Authorization Code flow for
// both the Accounting and Payments Data Sources. Later phases will add
// the actual charge/refund calls.
export const quickbooksAPI = {
  status: () => api.get('/quickbooks/status'),
  authUrl: () => api.get('/quickbooks/auth-url'),
  disconnect: () => api.post('/quickbooks/disconnect'),
  monitoring: () => api.get('/quickbooks/monitoring'),
};

// Phase 27.50.B — Storefront + Cart
export const shopAPI = {
  listProducts:   () => api.get('/shop/products'),
  getProduct:     (id) => api.get(`/shop/products/${id}`),
  getCart:        () => api.get('/cart'),
  addItem:        (payload) => api.post('/cart/items', payload),
  updateItem:     (itemId, payload) => api.patch(`/cart/items/${itemId}`, payload),
  removeItem:     (itemId) => api.delete(`/cart/items/${itemId}`),
  clearCart:      () => api.delete('/cart'),
  applyCoupon:    (payload) => api.post('/cart/coupon', payload),
  removeCoupon:  () => api.delete('/cart/coupon'),
  preparePayment: () => api.post('/cart/prepare-payment'),
  chargeCart:     (payload) => api.post('/cart/charge', payload),
  listOrders:     () => api.get('/orders'),
  // Phase 27.50.B.6 — Order history extras
  receiptBlob:    (orderId) => api.get(`/orders/${orderId}/receipt.pdf`, { responseType: 'blob' }),
  reorder:        (orderId) => api.post(`/orders/${orderId}/reorder`),
  // Special Orders (admin-created, agent-paid)
  getSpecialOrder:      (orderId) => api.get(`/shop/special-orders/${orderId}`),
  specialPreparePayment:(orderId) => api.post(`/shop/special-orders/${orderId}/prepare-payment`),
  specialCharge:        (orderId, payload) => api.post(`/shop/special-orders/${orderId}/charge`, payload),
};

// Phase 27.50.C — Admin storefront management
export const shopAdminAPI = {
  getSettings:    () => api.get('/admin/shop/settings'),
  updateSettings: (payload) => api.put('/admin/shop/settings', payload),
  listCoupons:    () => api.get('/admin/coupons'),
  listShopCategories: () => api.get('/admin/shop/categories'),
  createCoupon:   (payload) => api.post('/admin/coupons', payload),
  updateCoupon:   (code, payload) => api.patch(`/admin/coupons/${code}`, payload),
  deleteCoupon:   (code) => api.delete(`/admin/coupons/${code}`),
  listProducts:   () => api.get('/admin/shop/products'),
  bulkStatus:     (payload) => api.post('/admin/shop/products/bulk-status', payload),
  // Phase 27.56 — Orders tab (multi-payment)
  listOrders:     (params) => api.get('/admin/shop/orders', { params }),
  ordersSummary:  () => api.get('/admin/shop/orders/summary'),
  getOrder:       (orderId) => api.get(`/admin/shop/orders/${orderId}`),
  refundOrder:    (orderId, payload) => api.post(`/admin/shop/orders/${orderId}/refund`, payload),
  updateOrder:    (orderId, payload) => api.patch(`/admin/shop/orders/${orderId}`, payload),
  deleteOrder:    (orderId) => api.delete(`/admin/shop/orders/${orderId}`),
  // Special Orders (admin-created, agent-paid)
  createSpecialOrder: (payload) => api.post('/admin/shop/special-orders', payload),
  setSpecialStatus:   (orderId, special_status) => api.post(`/admin/shop/special-orders/${orderId}/status`, { special_status }),
};

// Contract Status Import — bulk-update carrier appointment statuses from a report
export const contractStatusImportAPI = {
  fields:  () => api.get('/contracts/status-import/fields'),
  preview: (formData) => api.post('/contracts/status-import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  resolve: (payload) => api.post('/contracts/status-import/resolve', payload),
  commit:  (payload) => api.post('/contracts/status-import/commit', payload),
  review:  () => api.get('/contracts/status-import/review'),
  resolveReview: (reviewId, payload) => api.post(`/contracts/status-import/review/${reviewId}/resolve`, payload),
  history: () => api.get('/contracts/status-import/history'),
  revertImport: (batchId) => api.post(`/contracts/status-import/history/${batchId}/revert`),
};

// Phase 27.56 — Multi-payment provider APIs (PayPal / Afterpay / Klarna scaffolding)
export const shopPaymentsAPI = {
  listProviders:      () => api.get('/shop/payments/providers'),
  paypalCreateOrder:  (payload) => api.post('/shop/paypal/create-order', payload || {}),
  paypalCapture:      (payload) => api.post('/shop/paypal/capture', payload),
  afterpayCreateCheckout: (payload) => api.post('/shop/afterpay/create-checkout', payload),
  afterpayConfirm:    (payload) => api.post('/shop/afterpay/confirm', payload),
  klarnaCreateSession:(payload) => api.post('/shop/klarna/create-session', payload || {}),
  klarnaCreateOrder:  (payload) => api.post('/shop/klarna/create-order', payload),
  specialPaypalCreate:(orderId, payload) => api.post(`/shop/special-orders/${orderId}/paypal/create-order`, payload || {}),
};

// Phase 27.50.B.10 — Deletion audit log & restore (KR admin only)
export const deletionAuditAPI = {
  list:    (params) => api.get('/admin/deletion-log', { params }),
  restore: (auditId) => api.post(`/admin/deletion-log/${auditId}/restore`),
};

// Phase 27.54 — Event Equipment & Supplies (KR admin only).
// Phase 1: inventory CRUD for equipment types, assets and locations,
// plus a fast barcode lookup used by the ScannerInput component.
export const suppliesAPI = {
  summary:            () => api.get('/supplies/summary'),
  // Types
  listTypes:          (params) => api.get('/supplies/types', { params }),
  createType:         (payload) => api.post('/supplies/types', payload),
  updateType:         (typeId, payload) => api.patch(`/supplies/types/${typeId}`, payload),
  deleteType:         (typeId) => api.delete(`/supplies/types/${typeId}`),
  uploadTypePhoto:    (typeId, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/supplies/types/${typeId}/photo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteTypePhoto:    (typeId) => api.delete(`/supplies/types/${typeId}/photo`),
  typePhotoBlob:      (typeId) => api.get(`/supplies/types/${typeId}/photo`, { responseType: 'blob' }),
  // Locations
  listLocations:      (params) => api.get('/supplies/locations', { params }),
  createLocation:     (payload) => api.post('/supplies/locations', payload),
  updateLocation:     (locationId, payload) => api.patch(`/supplies/locations/${locationId}`, payload),
  deleteLocation:     (locationId) => api.delete(`/supplies/locations/${locationId}`),
  // Assets
  listAssets:         (params) => api.get('/supplies/assets', { params }),
  getAsset:           (assetId) => api.get(`/supplies/assets/${assetId}`),
  getAssetByBarcode:  (ean13) => api.get(`/supplies/assets/by-barcode/${ean13}`),
  createAsset:        (payload) => api.post('/supplies/assets', payload),
  updateAsset:        (assetId, payload) => api.patch(`/supplies/assets/${assetId}`, payload),
  deleteAsset:        (assetId) => api.delete(`/supplies/assets/${assetId}`),
  uploadAssetPhoto:   (assetId, file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/supplies/assets/${assetId}/photo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAssetPhoto:   (assetId) => api.delete(`/supplies/assets/${assetId}/photo`),
  assetPhotoBlob:     (assetId) => api.get(`/supplies/assets/${assetId}/photo`, { responseType: 'blob' }),
  // Phase 3 add-ons
  overdueLoansCount:  () => api.get('/supplies/loans/overdue-count'),
  getTemplate:        () => api.get('/supplies/template'),
  updateTemplate:     (payload) => api.patch('/supplies/template', payload),
  templateLogoBlob:   () => api.get('/supplies/template/logo', { responseType: 'blob' }),
  uploadTemplateLogo: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return api.post('/supplies/template/logo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteTemplateLogo: () => api.delete('/supplies/template/logo'),
  previewTemplate:    (payload) => api.post('/supplies/template/preview', payload, { responseType: 'blob' }),
};

export const urgentTasksAPI = {
  active:   () => api.get('/urgent-tasks/active'),
  list:     (params) => api.get('/urgent-tasks', { params }),
  meta:     () => api.get('/urgent-tasks/meta'),
  create:   (payload) => api.post('/urgent-tasks', payload),
  complete: (id) => api.post(`/urgent-tasks/${id}/complete`),
  remove:   (id) => api.delete(`/urgent-tasks/${id}`),
};





